import { Link } from "wouter";
import { useListBanners, useListProducts } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useI18n } from "@/lib/i18n";
import { usePageMeta } from "@/lib/usePageMeta";
import { useDarkMode } from "@/lib/dark-mode";
import { ShieldCheck, Truck, RefreshCw, CreditCard, ChevronLeft, ChevronRight, Megaphone, Gift, Clock, Bell, Star, User, Heart, Tag, ShoppingBag, X } from "lucide-react";
import { useState, useEffect, useCallback, useRef, useMemo, useSyncExternalStore, lazy, Suspense, type ReactElement } from "react";
import { ProductCard as SharedProductCard } from "@/components/ProductCard";
import { buildSrcSet, buildAnySrcSet, resolveImageSrc, HERO_SIZES, CATEGORY_SIZES, withVersion, canonicalizeImageUrl } from "@/lib/imgSrc";
import { useTheme } from "@/lib/theme-context";
import { pickReadableTextColor } from "@/lib/contrast";

const LazyRecentlyViewed = lazy(() => import("@/components/RecentlyViewed").then(m => ({ default: m.RecentlyViewed })));
const LazyStoreStatsBadges = lazy(() => import("@/components/StoreStatsBadges").then(m => ({ default: m.StoreStatsBadges })));
const LazyLatestBlogPosts = lazy(() => import("@/components/LatestBlogPosts").then(m => ({ default: m.LatestBlogPosts })));
const LazyFeaturedBrands = lazy(() => import("@/components/FeaturedBrands").then(m => ({ default: m.FeaturedBrands })));
const LazyTestimonialsSection = lazy(() => import("@/components/TestimonialsSection").then(m => ({ default: m.TestimonialsSection })));
const LazyInstagramFeed = lazy(() => import("@/components/InstagramFeed").then(m => ({ default: m.InstagramFeed })));
const LazyCategoryWall = lazy(() => import("@/components/CategoryWall").then(m => ({ default: m.CategoryWall })));
const LazyAuctionSection = lazy(() => import("@/components/AuctionSection").then(m => ({ default: m.AuctionSection })));
const LazyFlashSalesSection = lazy(() => import("@/components/FlashSalesSection").then(m => ({ default: m.FlashSalesSection })));
const LazyCustomBlock = lazy(() => import("@/components/CustomBlock").then(m => ({ default: m.CustomBlock })));

const BASE_URL = import.meta.env.BASE_URL || "/";

// Resolves the homepage spacing preset (admin-controlled in
// /admin/appearance → Layout → "Module Heights") into the actual Tailwind
// classes used for both the inter-block wrapper gap and each block's own
// vertical padding. Centralizing this here means tweaking the px values
// later only needs one edit. Defaults to "compact" to match the May-2026
// density operators expect.
type SpacingPreset = "compact" | "normal" | "spacious";
function _spacing(preset: any): { wrapper: string; section: string; sectionTight: string } {
  const p: SpacingPreset = preset === "normal" || preset === "spacious" ? preset : "compact";
  // The last block on the homepage must NEVER leave a visible gap above the
  // footer — operators repeatedly flagged the empty band as "broken".
  // Force the bottom padding of the last child AND any nested <section>
  // descendant (covers renderSectionWithBg's div wrapper + LazySection's
  // div wrapper + the block's own <section>) to zero across all density
  // presets. The `_` in the arbitrary variant is the CSS descendant
  // combinator, so this matches at any depth, not just direct children.
  const noTailGap =
    "[&>*:last-child]:!pb-0 [&>*:last-child_section]:!pb-0 [&>*:last-child_>div]:!pb-0";
  if (p === "spacious") {
    return {
      wrapper: `flex flex-col gap-y-8 md:gap-y-14 ${noTailGap}`,
      section: "py-8 md:py-20",
      sectionTight: "py-6 md:py-14",
    };
  }
  if (p === "normal") {
    return {
      wrapper: `flex flex-col gap-y-5 md:gap-y-8 ${noTailGap}`,
      section: "py-5 md:py-12",
      sectionTight: "py-4 md:py-8",
    };
  }
  // Compact: the total vertical whitespace between two adjacent blocks
  // should match the height of the storefront "Add to cart" button
  // (~40–48px on desktop, ~32–36px on mobile). With wrapper gap-y-0 and
  // each section's py value applied on both sides, that means:
  //   desktop: 20 + 20 = 40px between two sections (sectionTight: 12 + 12 = 24px)
  //   mobile : 16 + 16 = 32px between two sections (sectionTight: 12 + 12 = 24px)
  return {
    wrapper: `flex flex-col gap-y-0 ${noTailGap}`,
    section: "py-4 md:py-5",
    sectionTight: "py-3 md:py-3",
  };
}

function _tabletCols(mobile: number, desktop: number): number {
  if (desktop <= mobile + 1) return desktop;
  return Math.round((mobile + desktop) / 2);
}
function _rGrid(cls: string, m: number, d: number): string {
  const t = _tabletCols(m, d);
  if (t === d) return `.${cls}{grid-template-columns:repeat(${m},1fr)}@media(min-width:768px){.${cls}{grid-template-columns:repeat(${d},1fr)}}`;
  return `.${cls}{grid-template-columns:repeat(${m},1fr)}@media(min-width:768px){.${cls}{grid-template-columns:repeat(${t},1fr)}}@media(min-width:1024px){.${cls}{grid-template-columns:repeat(${d},1fr)}}`;
}
function _rCar(cls: string, m: number, d: number): string {
  const t = _tabletCols(Math.floor(m), Math.floor(d));
  if (t === Math.floor(d)) return `.${cls} > *{flex:0 0 ${100/m}%;max-width:${100/m}%}@media(min-width:768px){.${cls} > *{flex:0 0 ${100/d}%;max-width:${100/d}%}}`;
  return `.${cls} > *{flex:0 0 ${100/m}%;max-width:${100/m}%}@media(min-width:768px){.${cls} > *{flex:0 0 ${100/t}%;max-width:${100/t}%}}@media(min-width:1024px){.${cls} > *{flex:0 0 ${100/d}%;max-width:${100/d}%}}`;
}
function _rSkel(cls: string, m: number, d: number): string {
  const t = _tabletCols(m, d);
  if (t === d) return `.${cls}{display:grid;gap:.75rem;grid-template-columns:repeat(${m},1fr)}@media(min-width:768px){.${cls}{gap:1rem;grid-template-columns:repeat(${d},1fr)}}`;
  return `.${cls}{display:grid;gap:.75rem;grid-template-columns:repeat(${m},1fr)}@media(min-width:768px){.${cls}{gap:1rem;grid-template-columns:repeat(${t},1fr)}}@media(min-width:1024px){.${cls}{grid-template-columns:repeat(${d},1fr)}}`;
}
function _rBP(cls: string, mP: number, dP: number): string {
  return `.${cls}{display:flex;gap:12px;align-items:stretch}.${cls} .bp-prods{flex:${mP};min-width:0;overflow:hidden}.${cls} .bp-bnr{flex:1;flex-shrink:0;min-width:0}@media(min-width:768px){.${cls} .bp-prods{flex:${dP}}}`;
}

type HomepageSectionCache = {
  id: number;
  type: string;
  title: string;
  titleAr: string | null;
  enabled: boolean;
  sortOrder: number;
  settings: Record<string, any>;
};

const SECTIONS_LS_KEY_HOME = "nx_homepage_sections_v1";
const sectionsCacheKey = (pageId: number | null | undefined, pageKey?: string | null) => {
  if (pageKey) return `nx_pagekey_sections_${pageKey}_v1`;
  if (pageId) return `nx_page_sections_${pageId}_v1`;
  return SECTIONS_LS_KEY_HOME;
};

function readSectionsFromStorage(key: string): HomepageSectionCache[] | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    return parsed;
  } catch { return null; }
}

const cachedSectionsByPage: Record<string, HomepageSectionCache[] | null> = {};
const sectionsPromiseByPage: Record<string, Promise<HomepageSectionCache[]> | null> = {};
const sectionsFetchFailedByPage: Record<string, boolean> = {};

function getCachedSections(pageId: number | null | undefined, pageKey?: string | null): HomepageSectionCache[] | null {
  const key = sectionsCacheKey(pageId, pageKey);
  if (cachedSectionsByPage[key] === undefined) {
    cachedSectionsByPage[key] = readSectionsFromStorage(key);
  }
  return cachedSectionsByPage[key] ?? null;
}

function getHomepageSections(pageId: number | null | undefined, pageKey?: string | null): Promise<HomepageSectionCache[]> {
  const key = sectionsCacheKey(pageId, pageKey);
  if (sectionsPromiseByPage[key]) return sectionsPromiseByPage[key]!;
  const url = pageKey
    ? `${BASE_URL}api/homepage-sections?pageKey=${encodeURIComponent(pageKey)}`
    : pageId
      ? `${BASE_URL}api/homepage-sections?pageId=${pageId}`
      : `${BASE_URL}api/homepage-sections`;
  const p = fetch(url)
    .then(r => r.json())
    .then(data => {
      const arr = Array.isArray(data) ? data : [];
      cachedSectionsByPage[key] = arr;
      try { localStorage.setItem(key, JSON.stringify(arr)); } catch {}
      return arr;
    })
    .catch(err => {
      sectionsFetchFailedByPage[key] = true;
      sectionsPromiseByPage[key] = null;
      throw err;
    });
  sectionsPromiseByPage[key] = p;
  return p;
}

function useInView(rootMargin = "200px") {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [rootMargin]);
  return { ref, inView };
}

function LazySection({ children, minHeight = "100px", eager = false }: { children: React.ReactNode; minHeight?: string; eager?: boolean }) {
  const { ref, inView } = useInView("300px");
  const visible = eager || inView;
  // CLS fix: reserve `minHeight` BEFORE the lazy content mounts so the
  // page doesn't shift downward when it appears. Once the content has
  // mounted, drop the floor (`empty:!min-h-0`) so that blocks which
  // resolve to nothing — e.g. a `category_products` block whose
  // category was just deleted, or one with zero matching products —
  // collapse cleanly instead of leaving an empty white box. Suspense
  // resolved to `null` produces no DOM children, so `:empty` matches.
  return (
    <div ref={ref} style={!visible ? { minHeight } : undefined} className="empty:!min-h-0">
      {visible ? <Suspense fallback={<div style={{ minHeight }} />}>{children}</Suspense> : null}
    </div>
  );
}

const mobileQuery = typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)") : null;
function subscribeToMobile(cb: () => void) {
  mobileQuery?.addEventListener("change", cb);
  return () => mobileQuery?.removeEventListener("change", cb);
}
function getIsMobile() { return mobileQuery?.matches ?? false; }

type HomepageSection = {
  id: number;
  type: string;
  title: string;
  titleAr: string | null;
  enabled: boolean;
  sortOrder: number;
  settings: Record<string, any>;
};

export default function Home({ pageId, pageKey }: { pageId?: number | null; pageKey?: string | null } = {}) {
  const isScopedPage = !!(pageId || pageKey);
  const { t, lang } = useI18n();
  // Theme palette for the active tenant. Used as a fallback for blocks that
  // don't have a per-block custom color set, so applying a theme template
  // (e.g. Natural Green) actually retints CTAs, service strip, audience tabs,
  // testimonials and stat badges across the whole homepage.
  const theme = useTheme();
  // Homepage uses the merchant's global SEO settings as-is. Passing no
  // overrides means: title becomes the merchant's `metaTitle` (or storeName
  // if blank); description/keywords/og:image fall back to the matching
  // global fields. Any save in /admin/settings updates the tab live because
  // the mutation cache is shared with the admin settings form.
  usePageMeta();
  const { data: banners, isLoading: isLoadingBanners } = useListBanners();

  const cacheKey = sectionsCacheKey(pageId, pageKey);
  const initialCached = getCachedSections(pageId, pageKey);
  const [sections, setSections] = useState<HomepageSection[]>(() => initialCached || []);
  const [sectionsLoaded, setSectionsLoaded] = useState(() => initialCached !== null);
  const [sectionsFailed, setSectionsFailed] = useState(() => !!sectionsFetchFailedByPage[cacheKey]);

  // Only fetch top-level featured/new-arrivals when a section actually uses them,
  // or when the sections fetch failed (fallback layout uses both).
  // While the sections list is still loading, keep both enabled so they run in
  // PARALLEL with the sections fetch (avoids a waterfall on cold page load).
  // Once sections resolve, the predicate tightens to the actual need and any
  // already-resolved cached data keeps serving.
  const needsFeatured =
    !sectionsLoaded ||
    sectionsFailed ||
    sections.some(
      (s) => s.enabled && (s.type === "featured_products" || s.type === "collections"),
    );
  const needsNewArrivals =
    !sectionsLoaded ||
    sectionsFailed ||
    sections.some((s) => s.enabled && s.type === "new_arrivals");
  const { data: productsData, isLoading: isLoadingProducts } = useListProducts(
    { limit: 20, featured: true, fields: "card" } as any,
    { query: { enabled: needsFeatured } } as any,
  );
  // "New Arrivals" must honor the merchant-curated list managed in
  // /admin/new-arrivals (manual picks first, then the auto time-window),
  // NOT a plain createdAt sort — otherwise manually-added products get
  // buried under bulk-imported newer rows and "never show up".
  const [newArrivalsList, setNewArrivalsList] = useState<any[] | null>(null);
  const [isLoadingNew, setIsLoadingNew] = useState(true);
  useEffect(() => {
    if (!needsNewArrivals) return;
    let active = true;
    setIsLoadingNew(true);
    fetch(`${BASE_URL}api/new-arrivals?limit=20`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : []))
      .then(data => { if (active) { setNewArrivalsList(Array.isArray(data) ? data : []); setIsLoadingNew(false); } })
      .catch(() => { if (active) { setNewArrivalsList([]); setIsLoadingNew(false); } });
    return () => { active = false; };
  }, [needsNewArrivals]);
  const newArrivals = newArrivalsList ? { products: newArrivalsList } : undefined;

  useEffect(() => {
    let active = true;
    // Always refetch in the background so admin toggles / new blocks
    // become visible without requiring users to clear their cache.
    // Cached data (if any) was already used for the instant first paint.
    sectionsPromiseByPage[cacheKey] = null;
    getHomepageSections(pageId, pageKey)
      .then(data => {
        if (!active) return;
        setSections(data);
        setSectionsLoaded(true);
        setSectionsFailed(false);
      })
      .catch(() => {
        if (!active) return;
        if (!getCachedSections(pageId, pageKey)) {
          setSectionsFailed(true);
        }
        setSectionsLoaded(true);
      });
    return () => { active = false; };
  }, [pageId, pageKey, cacheKey]);

  const isMobile = useSyncExternalStore(subscribeToMobile, getIsMobile, () => false);
  const { isDark } = useDarkMode();

  const mergeVariants = (settings: any) => {
    const all = settings || {};
    const { mobile = {}, dark = {}, mobileDark = {}, ...base } = all;
    let s: any = { ...base };
    if (isDark) s = { ...s, ...dark };
    if (isMobile) s = { ...s, ...mobile };
    if (isMobile && isDark) s = { ...s, ...mobileDark };
    return s;
  };

  // Exclude banners whose image was cleared (image_url emptied when the
  // merchant deletes that image from the library). Banners always require an
  // image (admin form enforces it), so an empty imageUrl means "no image" and
  // must not render a broken <img>.
  const activeBanners = banners?.filter(b => b.status === "active" && typeof b.imageUrl === "string" && b.imageUrl.trim() !== "") || [];

  const enabledSections = sections
    .filter(s => {
      const merged = mergeVariants(s.settings);
      // Device target gating: skip rendering entirely (no API calls, no JS, no images)
      // when section is restricted to the other device.
      const target = (s.settings as any)?.deviceTarget || merged.deviceTarget || "both";
      if (target === "desktop" && isMobile) return false;
      if (target === "mobile" && !isMobile) return false;
      if (merged.enabled !== undefined) return merged.enabled;
      return s.enabled;
    })
    .sort((a, b) => {
      // Sort exclusively by the top-level `sortOrder` column — the single
      // source of truth shown in the admin homepage-layout. Honoring
      // `settings.mobile.sortOrder` (and `dark.sortOrder`) here used to
      // scramble the mobile order whenever a tenant had partial variant
      // overrides: e.g. tenant_5 had testimonials.mobile=16 and
      // trust_badges.mobile=18 from old drag sessions, while neighboring
      // sections fell back to top-level values on a different scale,
      // causing banner_block (top=14) and category_wall (top=17) to slip
      // between testimonials and trust_badges on mobile. The per-variant
      // sort feature is intentionally ignored — variant settings still
      // apply to every other prop (visibility, columns, padding, etc.).
      return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    });

  // Audience tab gating: maintain the active tab id per audience_tabs block.
  // For each section, find the most recent preceding audience_tabs block; if
  // that block has tabs and the section's audienceTab is set (and not "all"),
  // only show the section when its tab matches the active one.
  const [audienceActive, setAudienceActive] = useState<Record<number, string>>({});
  const audienceScopeMap: Record<number, { blockId: number; tabs: any[] } | null> = {};
  {
    let currentAud: { blockId: number; tabs: any[] } | null = null;
    for (const sec of enabledSections) {
      if (sec.type === "audience_tabs") {
        const tabs = (mergeVariants(sec.settings)?.tabs as any[]) || [];
        currentAud = tabs.length > 0 ? { blockId: sec.id, tabs } : null;
      }
      audienceScopeMap[sec.id] = currentAud;
    }
  }
  // A section is "effectively empty" when its admin configuration has no
  // renderable content at all (no items, no tabs, no text, no category
  // binding). Without this filter such sections still mount inside a
  // LazySection wrapper that reserves ~100px as a CLS floor, and several
  // of them stacked together produce a large visible blank gap on the
  // homepage (production audit, May 2026 — luxurygive.com had
  // tabbed_categories with [] tabs sitting between the upper banners and
  // the "تسوق للرجال" circles, leaving a ~250px void). We exclude them
  // from rendering entirely. Sections whose content is fetched at
  // runtime (collections, featured_products, banners, …) cannot be
  // pre-judged here, so we leave them alone.
  const isEffectivelyEmpty = (sec: HomepageSection): boolean => {
    const s: any = mergeVariants(sec.settings) || {};
    const arr = (v: any) => Array.isArray(v) ? v : [];
    switch (sec.type) {
      case "tabbed_categories":
        return arr(s.tabs).length === 0;
      case "custom_block":
        // A custom_block needs either tabs OR a bound category to fetch.
        return arr(s.tabs).length === 0 && !s.categoryId;
      case "service_features":
      case "why_us":
        return arr(s.items).length === 0;
      case "trust_badges":
        // Trust badges + store stats are fetched at runtime from
        // /api/trust-badges and /api/store-stats (managed in
        // /admin/store-features), not from this block's settings.
        // Let the renderer decide if it has nothing to show.
        return false;
      case "about_brand":
        // The AboutBrand renderer reads descriptionEn/descriptionAr/logoUrl;
        // older data sometimes uses text/textAr. Treat the block as empty
        // only when ALL of those are blank — otherwise we silently drop a
        // configured section (the merchant's about_brand had descriptionAr
        // filled but text/textAr empty, so the block never appeared).
        return !String(s.descriptionEn || "").trim()
          && !String(s.descriptionAr || "").trim()
          && !String(s.text || "").trim()
          && !String(s.textAr || "").trim()
          && !String(s.logoUrl || "").trim();
      case "notification_bars":
        return arr(s.bars).length === 0;
      case "custom_circles":
        return arr(s.items).length === 0;
      case "audience_tabs":
        // Empty tabs => the section renders nothing, but we keep it so the
        // gating loop above still recognises the block id. Render-side
        // already returns null for empty tabs, so no extra space.
        return false;
      default:
        return false;
    }
  };

  const visibleSections = enabledSections.filter(sec => {
    // Audience-tab gating
    if (sec.type !== "audience_tabs") {
      const scope = audienceScopeMap[sec.id];
      if (scope) {
        const wanted = (sec.settings as any)?.audienceTab || "all";
        if (wanted !== "all") {
          const active = audienceActive[scope.blockId] || scope.tabs[0]?.id;
          if (wanted !== active) return false;
        }
      }
    }
    // Drop sections that have no content to render — they would otherwise
    // hold their LazySection placeholder height and leave a visible gap.
    if (isEffectivelyEmpty(sec)) return false;
    return true;
  });

  const renderSection = (section: HomepageSection, index: number = 99) => {
    const s = mergeVariants(section.settings);
    const limit = s.limit;
    const sectionTitle = lang === "ar" ? (section.titleAr || section.title) : section.title;
    // First six visible sections render eagerly (skip IntersectionObserver gate)
    // so above-the-fold LCP image is in the initial DOM, not after JS observes it.
    // Many storefronts have audience_tabs/banner/circles/notification before the
    // first image-heavy section (collections / category_wall) — covering 6 ensures
    // the LCP candidate is never gated behind an observer.
    const eager = index < 6;
    const priority = index === 0;
    switch (section.type) {
      case "hero_banner": {
        // Determine the active audience tab for this hero (if it sits below an audience_tabs block)
        const scope = audienceScopeMap[section.id];
        const activeAudience = scope ? (audienceActive[scope.blockId] || scope.tabs[0]?.id || "") : "";
        const sectionBanners = activeBanners
          .filter((b: any) => b.sectionId === section.id || (!b.sectionId && section.id === 1))
          .filter((b: any) => {
            // Per-banner audience filter: parse buttonSettings.audienceTab
            if (!activeAudience) return true;
            const bs = b.buttonSettings ? (typeof b.buttonSettings === "string" ? (() => { try { return JSON.parse(b.buttonSettings); } catch { return {}; } })() : b.buttonSettings) : {};
            const wantedAud = bs?.audienceTab || "all";
            return wantedAud === "all" || wantedAud === activeAudience;
          });
        return <HeroBanner key={section.id} banners={sectionBanners} isLoading={isLoadingBanners} lang={lang} settings={s} themeBtnBg={theme?.primaryBtnBg || theme?.primaryColor} themeBtnText={theme?.primaryBtnText} />;
      }
      case "service_features":
        return <ServiceFeatures key={section.id} lang={lang} settings={s} themeBg={theme?.backgroundColor} />;
      case "featured_products":
        return (
          <ProductGrid
            key={section.id}
            title={sectionTitle || (lang === "ar" ? "المنتجات المميزة" : "Featured Collection")}
            products={productsData?.products.slice(0, limit || 12) || []}
            isLoading={isLoadingProducts}
            lang={lang}
            settings={s}
          />
        );
      case "new_arrivals":
        return (
          <ProductGrid
            key={section.id}
            title={sectionTitle || (lang === "ar" ? "وصل حديثاً" : "New Arrivals")}
            products={newArrivals?.products.slice(0, limit || 8) || []}
            isLoading={isLoadingNew}
            lang={lang}
            settings={{ bgColor: "gray", ...s }}
          />
        );
      case "featured_brands":
        return <LazySection key={section.id} eager={eager} minHeight="200px"><LazyFeaturedBrands settings={s} /></LazySection>;
      case "collections":
        return <LazySection key={section.id} eager={eager} minHeight="400px"><CollectionsSection products={productsData?.products || []} lang={lang} settings={s} priority={priority} /></LazySection>;
      case "about_brand":
        return <LazySection key={section.id} eager={eager}><AboutBrand lang={lang} settings={s} title={sectionTitle} /></LazySection>;
      case "blog_posts":
        return <LazySection key={section.id} eager={eager}><LazyLatestBlogPosts settings={s} /></LazySection>;
      case "why_us":
        return <LazySection key={section.id} eager={eager}><WhyUs lang={lang} settings={s} title={sectionTitle} /></LazySection>;
      case "trust_badges":
        return <LazySection key={section.id} eager={eager}><LazyStoreStatsBadges settings={s} /></LazySection>;
      case "testimonials":
        return <LazySection key={section.id} eager={eager}><LazyTestimonialsSection settings={s} /></LazySection>;
      case "instagram_feed":
        return <LazySection key={section.id} eager={eager}><LazyInstagramFeed settings={s} /></LazySection>;
      case "recently_viewed":
        return <LazySection key={section.id} eager={eager}><LazyRecentlyViewed settings={s} /></LazySection>;
      case "category_wall":
        return <LazySection key={section.id} eager={eager} minHeight="400px"><LazyCategoryWall settings={s} priority={priority} /></LazySection>;
      case "auction":
        return <LazySection key={section.id} eager={eager} minHeight="300px"><LazyAuctionSection settings={s} /></LazySection>;
      case "flash_sales":
        return <LazySection key={section.id} eager={eager} minHeight="300px"><div className="container mx-auto px-4 py-8"><LazyFlashSalesSection /></div></LazySection>;
      case "banner_block":
        return <LazySection key={section.id} minHeight="200px" eager={eager}><BannerBlock settings={s} /></LazySection>;
      case "custom_banner":
        return <LazySection key={section.id} minHeight="200px" eager={eager}><BannerBlock settings={s} /></LazySection>;
      case "text_block":
        return <TextBlockRender key={section.id} settings={s} lang={lang} />;
      case "custom_circles":
        return <LazySection key={section.id} minHeight="180px" eager={eager}><CustomCirclesRender settings={s} lang={lang} /></LazySection>;
      case "promo_strip":
        return <PromoStripBlock key={section.id} settings={s} lang={lang} />;
      case "notification_bars":
        return <NotificationBarsBlock key={section.id} settings={s} lang={lang} />;
      case "audience_tabs": {
        const tabs: any[] = Array.isArray(s.tabs) ? s.tabs : [];
        const active = audienceActive[section.id] || tabs[0]?.id || "";
        return (
          <AudienceTabsBlock
            key={section.id}
            settings={s}
            lang={lang}
            active={active}
            onChange={(id) => setAudienceActive(prev => ({ ...prev, [section.id]: id }))}
            themePrimary={theme?.primaryColor}
          />
        );
      }
      case "category_products":
        return <LazySection key={section.id} eager={eager} minHeight="400px"><CategoryProductsBlock title={sectionTitle} settings={s} lang={lang} /></LazySection>;
      case "app_download":
        return <LazySection key={section.id} minHeight="200px" eager={eager}><AppDownloadBlock settings={s} lang={lang} /></LazySection>;
      case "tabbed_categories":
        return <LazySection key={section.id} eager={eager} minHeight="300px"><TabbedCategoriesBlock settings={s} lang={lang} /></LazySection>;
      case "banner_products":
        return <LazySection key={section.id} eager={eager} minHeight="300px"><BannerProductsBlock title={sectionTitle} settings={s} lang={lang} isFirst={index === 0} /></LazySection>;
      case "multi_banners":
        return <LazySection key={section.id} eager={eager} minHeight="200px"><MultiBannersBlock settings={s} lang={lang} /></LazySection>;
      case "custom_block":
        return <LazySection key={section.id} minHeight="200px" eager={eager}><LazyCustomBlock settings={s} title={section.title} titleAr={section.titleAr} lang={lang} /></LazySection>;
      default:
        return null;
    }
  };

  const renderSectionWithBg = (section: HomepageSection, index: number = 99) => {
    const node = renderSection(section, index);
    if (!node) return null;
    // Block colors are GLOBAL only (admin no longer exposes a per-device
    // toggle for them). Read from `section.settings` directly instead of
    // `mergeVariants(...)` so a stale `mobile.sectionBg` left over from the
    // old per-device editor cannot override what the merchant sees on
    // mobile after they set the desktop background to transparent.
    const s = (section.settings || {}) as any;
    const lightBg: string = s.sectionBg && s.sectionBg !== "transparent" ? s.sectionBg : "";
    const lightText: string = s.sectionTextLight || "";
    const darkBg: string = s.sectionBgDark || "";
    const darkText: string = s.sectionTextDark || "";
    const hasAny = !!(lightBg || lightText || darkBg || darkText);
    if (!hasAny) return node;
    // Many block components (Testimonials, RecentlyViewed, InstagramFeed,
    // StoreStatsBadges, NewArrivals, ServiceFeatures, etc.) render their
    // own <section> with a hard-coded Tailwind `bg-*` class that visually
    // covers the wrapper bg. The arbitrary-variant selector below forces
    // any descendant <section> background to be transparent so the
    // merchant-picked Block Background is the one the customer actually
    // sees, regardless of what each block component sets internally.
    const cls = `blk-c-${section.id}`;
    // Defensive sanitiser: the four colour values are merchant-typed
    // and get interpolated into a <style> tag, so a stray `}` or `;`
    // could break out of the rule. Only allow characters that legally
    // appear in CSS colour values (hex, rgb()/rgba()/hsl(), named
    // colours, `transparent`, `inherit`, etc.).
    const safeColor = (v: string) => /^[#a-zA-Z0-9 ,.\-%()/]+$/.test(v) ? v : "";
    const sLightBg = safeColor(lightBg);
    const sLightText = safeColor(lightText);
    const sDarkBg = safeColor(darkBg);
    const sDarkText = safeColor(darkText);
    // Several block components hard-code their heading colour with an
    // inline `style={{ color: "var(--block-text, var(--theme-heading-color))" }}`, which
    // wins over a normal `color:` cascade. We expose the merchant's
    // chosen text colour as a custom property `--block-text` too, and
    // those headings use `var(--block-text, var(--theme-heading-color))`
    // so the override cascades into them as well.
    const lightCss = `.${cls}{${sLightBg ? `background-color:${sLightBg};` : ""}${sLightText ? `color:${sLightText};--block-text:${sLightText};` : ""}}`;
    const darkCss = (sDarkBg || sDarkText)
      ? `:where(html.dark) .${cls}{${sDarkBg ? `background-color:${sDarkBg};` : ""}${sDarkText ? `color:${sDarkText};--block-text:${sDarkText};` : ""}}`
      : "";
    return (
      <div key={`bg-${section.id}`} className={`${cls} [&_section]:!bg-transparent`}>
        {(lightCss || darkCss) && <style>{`${lightCss}${darkCss}`}</style>}
        {node}
      </div>
    );
  };

  if (!sectionsLoaded) {
    if (isScopedPage) return null;
    // CLS fix (May 2026, luxurygive.com PageSpeed run): don't render
    // a HeroBanner placeholder here. Real layouts often place
    // notification_bar / audience_tabs BEFORE the hero, so once
    // sections resolve the hero placeholder gets pushed down by
    // ~80–120px, attributing a 0.13 layout shift to <main>. An
    // empty min-height container reserves vertical space without
    // anchoring any child to a position that will later move.
    return <div style={{ minHeight: "100vh" }} aria-busy="true" />;
  }

  if (sectionsFailed && enabledSections.length === 0) {
    if (isScopedPage) return null;
    return (
      <div className="flex flex-col">
        <HeroBanner banners={activeBanners} isLoading={isLoadingBanners} lang={lang} settings={{}} themeBtnBg={theme?.primaryBtnBg || theme?.primaryColor} themeBtnText={theme?.primaryBtnText} />
        <ServiceFeatures lang={lang} settings={{}} themeBg={theme?.backgroundColor} />
        <LazySection><div className="container mx-auto px-4 py-8"><LazyFlashSalesSection /></div></LazySection>
        <ProductGrid title={lang === "ar" ? "المنتجات المميزة" : "Featured Collection"} products={productsData?.products || []} isLoading={isLoadingProducts} lang={lang} settings={{}} />
        <ProductGrid title={lang === "ar" ? "وصل حديثاً" : "New Arrivals"} products={newArrivals?.products.slice(0, 8) || []} isLoading={isLoadingNew} lang={lang} settings={{ bgColor: "gray" }} />
        <LazySection><LazyCategoryWall settings={{}} /></LazySection>
        <LazySection><LazyFeaturedBrands settings={{}} /></LazySection>
        <LazySection><CollectionsSection products={productsData?.products || []} lang={lang} settings={{}} /></LazySection>
        <LazySection><AboutBrand lang={lang} settings={{}} /></LazySection>
        <LazySection><LazyTestimonialsSection settings={{}} /></LazySection>
        <LazySection><LazyLatestBlogPosts settings={{}} /></LazySection>
        <LazySection><LazyInstagramFeed settings={{}} /></LazySection>
        <LazySection><WhyUs lang={lang} settings={{}} /></LazySection>
        <LazySection><LazyStoreStatsBadges settings={{}} /></LazySection>
        <LazySection><LazyRecentlyViewed settings={{}} /></LazySection>
      </div>
    );
  }

  return (
    // Uniform vertical spacing between every block. Each block component
    // still renders its own internal padding for the actual content,
    // but the gap-y here guarantees an equal, predictable distance
    // between two adjacent blocks regardless of which block types sit
    // next to each other (so the page no longer alternates between
    // tight and huge gaps depending on which two blocks are adjacent).
    <>
      <div className={_spacing(theme?.homepageBlockSpacing).wrapper}>
        {visibleSections.map((section, index) => {
          if (section.type === "audience_tabs") {
            return renderSection(section, index);
          }
          return renderSectionWithBg(section, index);
        })}
      </div>
    </>
  );
}

function HeroBanner({ banners, isLoading, lang, settings, themeBtnBg, themeBtnText }: { banners: any[]; isLoading: boolean; lang: string; settings?: Record<string, any>; themeBtnBg?: string; themeBtnText?: string }) {
  const theme = useTheme();
  const bannerRadius = (theme as any)?.bannerRadius || "0px";
  const [currentSlide, setCurrentSlide] = useState(0);
  const [naturalRatios, setNaturalRatios] = useState<Record<string, number>>({});
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isRTL = lang === "ar";
  const s = settings || {};
  const heightDesktop = s.heightDesktop || "85";
  const heightMobile = s.heightMobile || "50";
  const showArrows = s.showArrows !== false;
  const showDots = s.showDots !== false;
  const showOverlay = s.showOverlay !== false;
  const dotsPosition = s.dotsPosition || "right";
  const autoDelay = Number(s.autoPlayDelay || 5) * 1000;
  // Section-level fit mode. New `fitMode` ("cover" | "contain" | "natural")
  // supersedes legacy `objectFit` ("cover" | "contain"). "natural" means the
  // banner adapts to each image's own aspect ratio so it never crops AND
  // leaves no empty bands. Per-banner override lives in
  // `banner.buttonSettings.fitMode` and beats the section default.
  const sectionFit: "cover" | "contain" | "natural" = (s.fitMode as any) || (s.objectFit === "contain" ? "contain" : "cover");
  // Outer spacing around the banner so the merchant can shrink it and have
  // whitespace on every side (works in cover/contain/natural alike).
  const paddingX = Math.max(0, Number(s.paddingX ?? 0));
  const paddingY = Math.max(0, Number(s.paddingY ?? 0));
  const sectionPadStyle: React.CSSProperties = (paddingX || paddingY)
    ? { paddingInline: `${paddingX}px`, paddingBlock: `${paddingY}px` }
    : {};
  const parseBs = (b: any) => {
    if (!b?.buttonSettings) return {};
    if (typeof b.buttonSettings !== "string") return b.buttonSettings;
    try { return JSON.parse(b.buttonSettings); } catch { return {}; }
  };
  const activeBanner = banners[currentSlide];
  const activeBs = parseBs(activeBanner);
  const activeFit: "cover" | "contain" | "natural" = (activeBs.fitMode as any) || sectionFit;
  // Both "natural" and "contain" should size the wrapper to the image's
  // own aspect ratio. Otherwise contain keeps the fixed hero height
  // (e.g. 50vh) and pads the image with large empty bands above/below
  // — which the merchant explicitly does NOT want. With aspectRatio
  // sizing the image fills the wrapper exactly, no empty space.
  const useImageRatio = activeFit === "natural" || activeFit === "contain";
  const activeRatio = activeBanner ? naturalRatios[String(activeBanner.id)] : undefined;

  const startAutoplay = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % (banners.length || 1));
    }, autoDelay);
  }, [banners.length, autoDelay]);

  useEffect(() => {
    if (banners.length > 1) startAutoplay();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [banners.length, startAutoplay]);

  const goTo = useCallback((index: number) => {
    setCurrentSlide(index);
    startAutoplay();
  }, [startAutoplay]);

  const goPrev = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + banners.length) % banners.length);
    startAutoplay();
  }, [banners.length, startAutoplay]);

  const goNext = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % banners.length);
    startAutoplay();
  }, [banners.length, startAutoplay]);

  // CLS fix: if there are zero banners after loading, returning a
  // bare `<section className="w-full">` collapses from the reserved
  // hero height (e.g. 35vh ≈ 280px on mobile) down to 0px, which
  // alone caused a ~0.10 layout shift on tenants that don't use the
  // hero block. Returning `null` removes the placeholder cleanly so
  // the next section flows up instead of jumping after collapse.
  if (!isLoading && banners.length === 0) return null;

  return (
    <section className="w-full" style={sectionPadStyle}>
      {isLoading ? (
        <div
          className="w-full bg-gray-100 animate-pulse hero-banner-h"
          style={{ "--hm": `${heightMobile}vh`, "--hd": `${heightDesktop}vh`, "--ht": `${Math.round((Number(heightMobile) + Number(heightDesktop)) / 2)}vh`, height: `var(--hm)`, borderRadius: bannerRadius } as any}
        >
          <style>{`.hero-banner-h{height:var(--hm)}@media(min-width:768px){.hero-banner-h{height:var(--ht,var(--hd))!important}}@media(min-width:1024px){.hero-banner-h{height:var(--hd)!important}}`}</style>
        </div>
      ) : banners.length > 0 ? (
        <div className="relative overflow-hidden" style={{ borderRadius: bannerRadius }}>
          <div
            className={`relative w-full ${useImageRatio ? "" : "hero-banner-h"}`}
            style={useImageRatio
              ? { aspectRatio: String(activeRatio || 16 / 9) }
              : ({ "--hm": `${heightMobile}vh`, "--hd": `${heightDesktop}vh`, "--ht": `${Math.round((Number(heightMobile) + Number(heightDesktop)) / 2)}vh`, height: `var(--hm)` } as any)
            }
          >
            {!useImageRatio && (<style>{`.hero-banner-h{height:var(--hm)}@media(min-width:768px){.hero-banner-h{height:var(--ht,var(--hd))!important}}@media(min-width:1024px){.hero-banner-h{height:var(--hd)!important}}`}</style>)}
            {banners.map((banner, idx) => {
              const bsOuter = banner.buttonSettings ? (typeof banner.buttonSettings === "string" ? (() => { try { return JSON.parse(banner.buttonSettings); } catch { return {}; } })() : banner.buttonSettings) : {};
              const perBannerRadius = typeof bsOuter.imageBorderRadius === "number" ? `${bsOuter.imageBorderRadius}px` : undefined;
              const perBannerFit: "cover" | "contain" | "natural" = (bsOuter.fitMode as any) || sectionFit;
              // In "natural" mode the wrapper aspect-ratio matches THIS image
              // when it's active, so object-contain produces zero letterbox.
              // For inactive slides with different ratios, contain still
              // prevents cropping (slim padding hidden behind opacity:0).
              const useContain = perBannerFit === "contain" || perBannerFit === "natural" || activeFit === "contain" || activeFit === "natural";
              return (
              <div
                key={banner.id}
                className="absolute inset-0 w-full h-full transition-opacity duration-700 ease-in-out overflow-hidden"
                style={{ opacity: idx === currentSlide ? 1 : 0, zIndex: idx === currentSlide ? 1 : 0, borderRadius: perBannerRadius }}
              >
                <img
                  src={resolveImageSrc(banner.imageUrl, 1920) || banner.imageUrl}
                  srcSet={buildAnySrcSet(banner.imageUrl, [640, 1080, 1280, 1600, 1920], [640, 1080, 1280, 1600, 1920])}
                  sizes={HERO_SIZES}
                  alt={(isRTL ? (banner.titleAr || banner.title) : (banner.title || banner.titleAr)) || (isRTL ? "بانر" : "Banner")}
                  className={`absolute inset-0 w-full h-full ${useContain ? "object-contain" : "object-cover"}`}
                  loading={idx === 0 ? "eager" : "lazy"}
                  fetchPriority={idx === 0 ? "high" : "low"}
                  decoding={idx === 0 ? "sync" : "async"}
                  onLoad={(e) => {
                    const t = e.currentTarget;
                    // NOTE: the Rich-Shell hero pre-paint overlay was removed
                    // (June 2026) because it caused a visible "image flashes
                    // then disappears" on tenant homepages. index.html now only
                    // preloads the hero bytes (invisible), so there is no
                    // overlay to hand off to here anymore.
                    if (!t.naturalWidth || !t.naturalHeight) return;
                    const r = t.naturalWidth / t.naturalHeight;
                    setNaturalRatios((m) => (m[String(banner.id)] === r ? m : { ...m, [String(banner.id)]: r }));
                  }}
                />
                {(() => {
                  const bs = banner.buttonSettings ? (typeof banner.buttonSettings === "string" ? JSON.parse(banner.buttonSettings) : banner.buttonSettings) : {};
                  const btnShow = bs.showButton !== false;
                  const btnText = isRTL ? (bs.buttonTextAr || bs.buttonText || "تسوق الآن") : (bs.buttonText || "SHOP NOW");
                  // Follow theme primary button when merchant hasn't picked a
                  // custom button color. This is what makes "Apply Theme
                  // Template" repaint hero CTAs in the new palette.
                  const btnBg = bs.buttonBgColor || themeBtnBg || "#ffffff";
                  const btnColor = bs.buttonTextColor || themeBtnText || "#000000";
                  const btnStyle = bs.buttonStyle || "square";
                  const btnRadius = btnStyle === "pill" ? "9999px" : btnStyle === "rounded" ? "0.5rem" : "0";
                  const isOutline = btnStyle === "outline";
                  const pos = bs.contentPosition || "bottom-right";
                  const [posV, posH] = pos.split("-");
                  const alignItems = posV === "top" ? "items-start pt-16 md:pt-24" : posV === "center" ? "items-center" : "items-end pb-16 md:pb-24";
                  const justifyContent = posH === "left" ? "justify-start" : posH === "center" ? "justify-center" : "justify-end";
                  const textAlign = posH === "left" ? "text-start" : posH === "center" ? "text-center" : "text-end";

                  const sizeMap: Record<string, string> = { xs: "text-xs", sm: "text-sm", base: "text-base", lg: "text-lg", xl: "text-xl", "2xl": "text-2xl", "3xl": "text-3xl md:text-5xl lg:text-6xl", "4xl": "text-4xl md:text-6xl lg:text-7xl", "5xl": "text-5xl md:text-7xl", "6xl": "text-6xl md:text-8xl" };
                  const weightMap: Record<string, string> = { light: "font-light", normal: "font-normal", medium: "font-medium", semibold: "font-semibold", bold: "font-bold" };
                  const subSizeMap: Record<string, string> = { xs: "text-xs", sm: "text-sm md:text-base", base: "text-base md:text-lg", lg: "text-lg md:text-xl", xl: "text-xl md:text-2xl", "2xl": "text-2xl md:text-3xl" };

                  const titleSizeCls = sizeMap[bs.titleSize || "3xl"] || sizeMap["3xl"];
                  const titleWeightCls = weightMap[bs.titleWeight || "light"] || "font-light";
                  const titleColor = bs.titleColor || "#ffffff";
                  const subtitleSizeCls = subSizeMap[bs.subtitleSize || "sm"] || subSizeMap["sm"];
                  const subtitleWeightCls = weightMap[bs.subtitleWeight || "light"] || "font-light";
                  const subtitleColor = bs.subtitleColor || "#cccccc";
                  const useHtmlTitle = bs.useHtmlTitle;
                  const useHtmlSubtitle = bs.useHtmlSubtitle;
                  const titlePos = bs.titlePosition || "same";
                  const subtitlePos = bs.subtitlePosition || "same";

                  const posClasses = (p: string) => {
                    const [v, h] = p.split("-");
                    const ai = v === "top" ? "items-start pt-12 md:pt-20" : v === "center" ? "items-center" : "items-end pb-12 md:pb-20";
                    const jc = h === "left" ? "justify-start" : h === "center" ? "justify-center" : "justify-end";
                    const ta = h === "left" ? "text-start" : h === "center" ? "text-center" : "text-end";
                    return { ai, jc, ta };
                  };

                  const titleContent = isRTL ? (banner.titleAr || banner.title) : banner.title;
                  const subtitleContent = isRTL ? (banner.subtitleAr || banner.subtitle) : banner.subtitle;

                  return (
                    <>
                      {!btnShow && banner.linkUrl && (
                        <Link href={banner.linkUrl}>
                          <div className="absolute inset-0 z-[2] cursor-pointer" />
                        </Link>
                      )}
                      {showOverlay && (
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/15 to-transparent">
                          {titlePos !== "same" && titleContent && (() => {
                            const tc = posClasses(titlePos);
                            return (
                              <div className={`absolute inset-0 flex ${tc.ai} ${tc.jc} pointer-events-none z-[1]`}>
                                <div className={`${tc.ta} px-8 md:px-16 max-w-2xl`}>
                                  {useHtmlTitle ? (
                                    <div className={`${titleSizeCls} ${titleWeightCls} tracking-[0.05em] leading-tight`} style={{ color: titleColor }} dangerouslySetInnerHTML={{ __html: titleContent }} />
                                  ) : idx === 0 ? (
                                    <h1 className={`${titleSizeCls} ${titleWeightCls} tracking-[0.05em] leading-tight`} style={{ color: titleColor }}>{titleContent}</h1>
                                  ) : (
                                    <h2 className={`${titleSizeCls} ${titleWeightCls} tracking-[0.05em] leading-tight`} style={{ color: titleColor }}>{titleContent}</h2>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                          {subtitlePos !== "same" && subtitleContent && (() => {
                            const sc = posClasses(subtitlePos);
                            return (
                              <div className={`absolute inset-0 flex ${sc.ai} ${sc.jc} pointer-events-none z-[1]`}>
                                <div className={`${sc.ta} px-8 md:px-16 max-w-md`}>
                                  {useHtmlSubtitle ? (
                                    <div className={`${subtitleSizeCls} ${subtitleWeightCls} tracking-wide leading-relaxed`} style={{ color: subtitleColor }} dangerouslySetInnerHTML={{ __html: subtitleContent }} />
                                  ) : (
                                    <p className={`${subtitleSizeCls} ${subtitleWeightCls} tracking-wide leading-relaxed`} style={{ color: subtitleColor }}>{subtitleContent}</p>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                          <div className={`absolute inset-0 flex ${alignItems} ${justifyContent}`}>
                            <div className={`${textAlign} px-8 md:px-16 max-w-2xl`}>
                              {titlePos === "same" && titleContent && (
                                useHtmlTitle ? (
                                  <div className={`${titleSizeCls} ${titleWeightCls} tracking-[0.05em] mb-3 leading-tight`} style={{ color: titleColor }} dangerouslySetInnerHTML={{ __html: titleContent }} />
                                ) : idx === 0 ? (
                                  <h1 className={`${titleSizeCls} ${titleWeightCls} tracking-[0.05em] mb-3 leading-tight`} style={{ color: titleColor }}>{titleContent}</h1>
                                ) : (
                                  <h2 className={`${titleSizeCls} ${titleWeightCls} tracking-[0.05em] mb-3 leading-tight`} style={{ color: titleColor }}>{titleContent}</h2>
                                )
                              )}
                              {subtitlePos === "same" && subtitleContent && (
                                useHtmlSubtitle ? (
                                  <div className={`${subtitleSizeCls} ${subtitleWeightCls} mb-6 tracking-wide leading-relaxed max-w-md`} style={{ color: subtitleColor }} dangerouslySetInnerHTML={{ __html: subtitleContent }} />
                                ) : (
                                  <p className={`${subtitleSizeCls} ${subtitleWeightCls} mb-6 tracking-wide leading-relaxed max-w-md`} style={{ color: subtitleColor }}>{subtitleContent}</p>
                                )
                              )}
                              {(() => {
                                const btn2Show = bs.button2Show === true;
                                const btn2Text = isRTL ? (bs.button2TextAr || bs.button2Text || "") : (bs.button2Text || bs.button2TextAr || "");
                                const btn2Url = bs.button2Url || banner.linkUrl;
                                const btn2Bg = bs.button2BgColor || "#000000";
                                const btn2Color = bs.button2TextColor || "#ffffff";
                                const btn2Style = bs.button2Style || "outline";
                                const btn2Radius = btn2Style === "pill" ? "9999px" : btn2Style === "rounded" ? "0.5rem" : "0";
                                const btn2Outline = btn2Style === "outline";
                                return (
                                  <div className="flex flex-wrap gap-3 items-center" style={{ justifyContent: posH === "left" ? "flex-start" : posH === "center" ? "center" : "flex-end" }}>
                                    {btnShow && banner.linkUrl && (
                                      <Link href={banner.linkUrl}>
                                        <button
                                          className="inline-flex items-center gap-2 px-8 py-3.5 text-xs tracking-[0.2em] uppercase font-medium transition-all duration-300 hover:opacity-80"
                                          style={{
                                            backgroundColor: isOutline ? "transparent" : btnBg,
                                            color: isOutline ? btnBg : btnColor,
                                            borderRadius: btnRadius,
                                            border: isOutline ? `2px solid ${btnBg}` : "none",
                                          }}
                                        >
                                          <span>{btnText}</span>
                                        </button>
                                      </Link>
                                    )}
                                    {btn2Show && btn2Text && btn2Url && (
                                      <Link href={btn2Url}>
                                        <button
                                          className="inline-flex items-center gap-2 px-8 py-3.5 text-xs tracking-[0.2em] uppercase font-medium transition-all duration-300 hover:opacity-80"
                                          style={{
                                            backgroundColor: btn2Outline ? "transparent" : btn2Bg,
                                            color: btn2Outline ? btn2Bg : btn2Color,
                                            borderRadius: btn2Radius,
                                            border: btn2Outline ? `2px solid ${btn2Bg}` : "none",
                                          }}
                                        >
                                          <span>{btn2Text}</span>
                                        </button>
                                      </Link>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              );
            })}
          </div>

          {banners.length > 1 && (
            <>
              {showArrows && (
                <>
                  <button
                    onClick={isRTL ? goNext : goPrev}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/10 backdrop-blur-sm text-white hover:bg-white/30 h-11 w-11 rounded-full flex items-center justify-center transition-all duration-300"
                    aria-label={lang === "ar" ? "البانر السابق" : "Previous banner"}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={isRTL ? goPrev : goNext}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/10 backdrop-blur-sm text-white hover:bg-white/30 h-11 w-11 rounded-full flex items-center justify-center transition-all duration-300"
                    aria-label={lang === "ar" ? "البانر التالي" : "Next banner"}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
              {showDots && (
                <div className={
                  dotsPosition === "bottom"
                    ? "absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-row gap-2 z-10"
                    : dotsPosition === "left"
                      ? "absolute left-6 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-10"
                      : "absolute right-6 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-10"
                }>
                  {banners.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => goTo(i)}
                      className="w-6 h-6 flex items-center justify-center"
                      aria-label={lang === "ar" ? `الانتقال للبانر ${i + 1} من ${banners.length}` : `Go to banner ${i + 1} of ${banners.length}`}
                      aria-current={i === currentSlide ? "true" : undefined}
                    >
                      <span
                        aria-hidden="true"
                        className={`block rounded-full transition-all duration-300 ${
                          i === currentSlide ? "w-2.5 h-2.5 bg-white" : "w-2 h-2 bg-white/40 hover:bg-white/70"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="relative h-[40vh] md:h-[50vh] w-full overflow-hidden bg-black">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white px-6 max-w-3xl">
              <Link href="/shop">
                <button className="inline-flex items-center gap-2 bg-white text-black px-8 py-3.5 text-xs tracking-[0.2em] uppercase font-medium hover:bg-gray-100 transition-colors duration-300">
                  <span>{isRTL ? "تسوق الآن" : "SHOP NOW"}</span>
                </button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function ServiceFeatures({ lang, settings, themeBg }: { lang: string; settings?: Record<string, any>; themeBg?: string }) {
  const s = settings || {};
  const ICON_MAP: Record<string, any> = { shield: ShieldCheck, truck: Truck, credit: CreditCard, refresh: RefreshCw };
  const defaultItems = [
    { iconType: "shield", labelEn: "Authentic Products", labelAr: "منتجات أصلية ومضمونة" },
    { iconType: "truck", labelEn: "Fast Shipping", labelAr: "شحن سريع لكل المدن" },
    { iconType: "credit", labelEn: "Interest-Free Installments", labelAr: "تقسيط بدون فوائد" },
    { iconType: "refresh", labelEn: "Safe Shopping", labelAr: "تجربة تسوق آمنة وموثوقة" },
  ];
  const items = s.items && s.items.length > 0 ? s.items : defaultItems;
  // Theme-bg fallback so this strip blends with an applied template instead
  // of staying stuck on the historical light-gray default.
  const bgColor = s.bgColor || themeBg || "#f9fafb";
  const columns = s.columns || "auto";
  const rowHeight = parseInt(s.rowHeight || "80", 10);
  const colCount = columns === "auto" ? items.length : parseInt(columns, 10);
  const gridId = `svcbar-${colCount}`;

  const isAutoRow = columns === "auto";

  return (
    <section className="py-0">
      {isAutoRow ? (
        <div className="flex" style={{ backgroundColor: bgColor }}>
          {items.map((f: any, i: number) => {
            const IconComp = ICON_MAP[f.iconType] || ShieldCheck;
            return (
              <div key={i} className="flex flex-col items-center justify-center gap-1 md:gap-2 border-e border-gray-100 last:border-e-0 flex-1" style={{ height: `${rowHeight}px` }}>
                <IconComp className="h-4 w-4 md:h-6 md:w-6 text-gray-500" />
                <span className="text-[8px] md:text-[11px] leading-tight tracking-wider text-gray-600 text-center px-1 md:px-2">{lang === "ar" ? (f.labelAr || f.labelEn) : f.labelEn}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <>
          <style>{_rGrid(gridId, Math.min(colCount, 2), colCount)}</style>
          <div className={`grid ${gridId}`}>
            {items.map((f: any, i: number) => {
              const IconComp = ICON_MAP[f.iconType] || ShieldCheck;
              return (
                <div key={i} className="flex flex-col items-center justify-center gap-3 border-e border-b border-gray-100 last:border-e-0" style={{ backgroundColor: bgColor, height: `${rowHeight}px` }}>
                  <IconComp className="h-6 w-6 text-gray-500" />
                  <span className="text-[11px] tracking-wider text-gray-600 text-center px-2">{lang === "ar" ? (f.labelAr || f.labelEn) : f.labelEn}</span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}

function ProductGrid({ title, products, isLoading, lang, settings, expectedCount }: {
  title: string; products: any[]; isLoading: boolean; lang: string; settings?: Record<string, any>; expectedCount?: number;
}) {
  const theme = useTheme();
  const s = settings || {};
  const displayStyle = s.displayStyle || "grid";
  const colsDesktop = Number(s.columnsPerRow) || 4;
  const colsMobile = Number(s.columnsPerRowMobile) || 2;
  const colsDesktopGrid = Math.max(1, Math.floor(colsDesktop));
  const colsMobileGrid = Math.max(1, Math.floor(colsMobile));
  const skeletonCount = expectedCount || s.limit || (colsDesktopGrid * 3);
  const showViewAll = s.showViewAll !== false;
  const viewAllLink = s.viewAllLink || "/shop";
  const viewAllText = lang === "ar" ? (s.viewAllTextAr || "عرض الكل") : (s.viewAllTextEn || "VIEW ALL");
  const bgColor = s.bgColor === "gray" ? "bg-gray-50" : "";
  const showArrows = s.showArrows !== false;
  // Per-block product image shape (circle / square / rectangle). Lets the
  // merchant present different visual treatments in different sections of
  // the homepage (e.g. round avatars for "Featured Brands", square tiles
  // for "Bestsellers"). Falls back to whatever the global theme decides.
  const { imageRatio: cardImageRatio, imageRadius: cardImageRadius } = (() => {
    const shape = String(s.imageShape || "").toLowerCase();
    // Square / rectangle leave radius undefined so ProductCard falls back to
    // the global theme corner radius (Admin → Appearance → Corner Radius).
    // Only "circle" hard-overrides to a full circle.
    if (shape === "circle") return { imageRatio: "1/1", imageRadius: "9999px" };
    if (shape === "square") return { imageRatio: "1/1", imageRadius: undefined as string | undefined };
    if (shape === "rectangle") return { imageRatio: "4/5", imageRadius: undefined as string | undefined };
    return { imageRatio: undefined as string | undefined, imageRadius: undefined as string | undefined };
  })();

  return (
    <section className={`${_spacing(theme?.homepageBlockSpacing).sectionTight} ${bgColor}`}>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <h2 className="text-lg md:text-xl font-semibold" style={{ color: "var(--block-text, var(--theme-heading-color))" }}>{title}</h2>
          <div>
            {showViewAll && (
              <Link href={viewAllLink} className="text-xs text-gray-500 hover:text-black transition-colors px-2 py-2 inline-block min-h-[44px]" aria-label={lang === "ar" ? `${viewAllText}: ${title}` : `${viewAllText}: ${title}`}>
                {viewAllText}
              </Link>
            )}
          </div>
        </div>
        {isLoading ? (
          <>
            <style>{_rSkel(`pgskel-m${colsMobileGrid}-d${colsDesktopGrid}`, colsMobileGrid, colsDesktopGrid)}</style>
            <div className={`pgskel-m${colsMobileGrid}-d${colsDesktopGrid}`} style={{ minHeight: `${Math.ceil(skeletonCount / colsDesktopGrid) * 360}px` }}>
              {Array.from({ length: skeletonCount }).map((_, i) => (
                // CLS fix: real ProductCard renders ~360px tall (square
                // image + title + price + button). Skeleton matches that
                // exactly: square placeholder + 3 short lines + button
                // bar so when products resolve, no row reflows. Previous
                // estimate (280px, 2 lines) was ~80px short per row and
                // contributed the second 0.04 CLS shift.
                <div key={i} className="space-y-2">
                  <div className="aspect-square bg-gray-100 animate-pulse rounded" />
                  <div className="h-3 bg-gray-100 animate-pulse w-2/3" />
                  <div className="h-3 bg-gray-100 animate-pulse w-1/3" />
                  <div className="h-8 bg-gray-100 animate-pulse rounded" />
                </div>
              ))}
            </div>
          </>
        ) : displayStyle === "carousel" ? (
          <>
            <style>{_rCar(`pgcar-m${String(colsMobile).replace(".","_")}-d${String(colsDesktop).replace(".","_")}`, colsMobile, colsDesktop).replace(/%/g, "% !important")}</style>
            <Carousel className="w-full" opts={{ align: "start", dragFree: true }}>
              <CarouselContent className={`-ml-3 pgcar-m${String(colsMobile).replace(".","_")}-d${String(colsDesktop).replace(".","_")}`}>
                {products.map((product, idx) => (
                  <CarouselItem key={product.id} className="pl-3">
                    <SharedProductCard product={product} priority={idx < 4} imageRatio={cardImageRatio} imageRadius={cardImageRadius} />
                  </CarouselItem>
                ))}
              </CarouselContent>
              {showArrows && <CarouselPrevious className="-left-4 bg-white border shadow-md" srLabel={lang === "ar" ? `السابق: ${title}` : `Previous: ${title}`} />}
              {showArrows && <CarouselNext className="-right-4 bg-white border shadow-md" srLabel={lang === "ar" ? `التالي: ${title}` : `Next: ${title}`} />}
            </Carousel>
          </>
        ) : (
          <>
            <style>{_rGrid(`pgrid-m${colsMobileGrid}-d${colsDesktopGrid}`, colsMobileGrid, colsDesktopGrid)}</style>
            <div className={`grid gap-3 md:gap-4 pgrid-m${colsMobileGrid}-d${colsDesktopGrid}`}>
              {products.map((product, idx) => (
                <SharedProductCard key={product.id} product={product} priority={idx < 4} imageRatio={cardImageRatio} imageRadius={cardImageRadius} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function CollectionsSection({ products, lang, settings, priority = false }: { products: any[]; lang: string; settings?: Record<string, any>; priority?: boolean }) {
  const theme = useTheme();
  const s = settings || {};
  const cols = s.columns || 2;
  const showButton = s.showButton !== false;
  const bannerRadius = s.imageRadius !== undefined && s.imageRadius !== null && s.imageRadius !== ""
    ? (typeof s.imageRadius === "number" ? `${s.imageRadius}px` : String(s.imageRadius))
    : ((theme as any)?.bannerRadius || "0px");
  // Default placeholders shown ONLY when the merchant hasn't configured
  // any items at all (fresh install / preview). We deliberately do NOT
  // pull images from the live products feed here, otherwise an unrelated
  // product image (e.g. one a merchant just uploaded) leaks into a
  // banner the merchant didn't fill in. Empty imageUrl → gray placeholder.
  const defaultItems = [
    { titleEn: "Men's Collection", titleAr: "نظارات رجالية", imageUrl: "", link: "/shop" },
    { titleEn: "Women's Collection", titleAr: "نظارات نسائية", imageUrl: "", link: "/shop" },
  ];
  const items = s.items && s.items.length > 0 ? s.items : defaultItems;

  return (
    <section className={_spacing(theme?.homepageBlockSpacing).section}>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <style>{_rGrid("coll-grid", 1, cols)}</style>
        <div className="coll-grid grid gap-3 md:gap-8">
          {items.map((item: any, idx: number) => (
            <div key={idx} className="relative group overflow-hidden bg-gray-100 aspect-[4/3]" style={{ borderRadius: bannerRadius }}>
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center z-10">
                <div className="text-center text-white p-6">
                  <h3 className="text-xl md:text-2xl font-light tracking-[0.2em] uppercase mb-4">
                    {lang === "ar" ? (item.titleAr || item.titleEn) : item.titleEn}
                  </h3>
                  {showButton && (
                    <Button asChild className="bg-white text-black hover:bg-gray-100 rounded-none px-8 py-4 text-xs tracking-[0.3em] uppercase">
                      <Link href={item.link || "/shop"}>{lang === "ar" ? "تسوق الآن" : "SHOP NOW"}</Link>
                    </Button>
                  )}
                </div>
              </div>
              {item.imageUrl && (() => {
                const url = item.imageUrl;
                // First card image is almost always the LCP for this section type,
                // regardless of the section's position on the page. Always prioritize.
                const isFirst = idx === 0;
                return (
                  <img
                    src={resolveImageSrc(url, 1080) || url}
                    srcSet={buildAnySrcSet(url, [400, 640, 800, 1080], [400, 640, 800, 1080])}
                    sizes="(min-width:1024px) 50vw, 100vw"
                    alt={item.title || ""}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading={isFirst ? "eager" : "lazy"}
                    fetchPriority={isFirst ? "high" : "auto"}
                    decoding={isFirst ? "sync" : "async"}
                  />
                );
              })()}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutBrand({ lang, settings, title }: { lang: string; settings: Record<string, any>; title?: string }) {
  const theme = useTheme();
  const s = settings || {};
  const bgColor = s.bgColor || "dark";
  const bgClass = bgColor === "dark" ? "bg-black text-white" : bgColor === "gray" ? "bg-gray-100 text-black" : "bg-white text-black";
  const logoUrl = s.logoUrl || "";
  const descEn = s.descriptionEn || "";
  const descAr = s.descriptionAr || "";
  const showButton = s.showButton !== false;
  const buttonLink = s.buttonLink || "/shop";
  const buttonTextEn = s.buttonTextEn || "DISCOVER COLLECTION";
  const buttonTextAr = s.buttonTextAr || "اكتشف المجموعة";
  const sectionTitle = title || "";

  return (
    <section className={`${bgClass} ${_spacing(theme?.homepageBlockSpacing).section}`}>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          {logoUrl ? (
            <img src={logoUrl} alt={sectionTitle} width={180} height={48} loading="lazy" decoding="async" className={`h-12 w-auto mx-auto mb-6 ${bgColor === "dark" ? "invert" : ""}`} />
          ) : null}
          {sectionTitle ? (
            <h2 className="text-xl md:text-2xl font-light tracking-[0.2em] uppercase mb-4" style={{ color: "var(--block-text, var(--theme-heading-color))" }}>{sectionTitle}</h2>
          ) : null}
          <p className={`text-sm leading-relaxed tracking-wider mb-8 ${bgColor === "dark" ? "text-gray-400" : "text-gray-600"}`}>
            {lang === "ar" ? descAr : descEn}
          </p>
          {showButton && (
            <Button asChild className={`rounded-none px-10 py-5 text-xs tracking-[0.3em] uppercase font-medium ${bgColor === "dark" ? "bg-white text-black hover:bg-gray-100" : "bg-black text-white hover:bg-gray-800"}`}>
              <Link href={buttonLink}>{lang === "ar" ? buttonTextAr : buttonTextEn}</Link>
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

function TextBlockRender({ settings, lang }: { settings: Record<string, any>; lang: string }) {
  const s = settings || {};
  const text = lang === "ar" ? (s.textAr || s.text || "") : (s.text || s.textAr || "");
  if (!text) return null;
  const bg = s.backgroundColor && s.backgroundColor !== "transparent" ? s.backgroundColor : "transparent";
  const decorations: string[] = [];
  if (s.underline) decorations.push("underline");
  return (
    <section
      className="w-full"
      style={{ backgroundColor: bg, paddingTop: Number(s.paddingV ?? 16), paddingBottom: Number(s.paddingV ?? 16) }}
    >
      <div className="max-w-7xl mx-auto" style={{ paddingLeft: Number(s.paddingH ?? 16), paddingRight: Number(s.paddingH ?? 16) }}>
        <div
          style={{
            fontSize: `${Number(s.fontSize ?? 22)}px`,
            fontWeight: String(s.fontWeight || "600"),
            fontStyle: s.italic ? "italic" : "normal",
            textDecoration: decorations.join(" ") || "none",
            color: s.color || "#1A1A1A",
            textAlign: (s.align || "center") as any,
            lineHeight: Number(s.lineHeight ?? 1.6),
            letterSpacing: `${Number(s.letterSpacing ?? 0)}px`,
            borderRadius: `${Number(s.borderRadius ?? 0)}px`,
            whiteSpace: "pre-wrap",
          }}
        >
          {text}
        </div>
      </div>
    </section>
  );
}

function CustomCirclesRender({ settings, lang }: { settings: Record<string, any>; lang: string }) {
  const s = settings || {};
  const items: any[] = Array.isArray(s.items) ? s.items : [];
  if (items.length === 0) return null;
  const columnsRaw = Math.max(2, Math.min(8, Number(s.columns) || 4));
  const isFractionalDesktop = columnsRaw % 1 !== 0;
  const columns = isFractionalDesktop ? Math.ceil(columnsRaw) : columnsRaw;
  const columnsMobileRaw = Math.max(2, Math.min(6, Number(s.columnsMobile) || 3));
  // Fractional values (e.g. 4.5, 5.5) auto-enable horizontal scroll so half a
  // circle "peeks" at the edge to hint the row is scrollable. The other
  // breakpoints still render as a grid using the integer ceiling.
  const isFractionalMobile = columnsMobileRaw % 1 !== 0;
  const columnsMobile = isFractionalMobile ? Math.ceil(columnsMobileRaw) : columnsMobileRaw;
  const circleSizeDesktop = Math.max(40, Math.min(200, Number(s.circleSizeDesktop ?? s.circleSize) || 80));
  const circleSizeMobile = Math.max(40, Math.min(200, Number(s.circleSizeMobile ?? s.circleSize) || 80));
  const rowsLimit = Math.max(0, Number(s.rows) || 0);
  const isCarousel = s.displayStyle === "carousel";
  // If a row cap (rows>=1) would silently hide items on mobile, fall back to
  // horizontal scroll instead of hiding them — operators expect every circle
  // they add to be reachable from the storefront.
  const mobileOverflow = !isCarousel && !isFractionalMobile && rowsLimit > 0 && items.length > rowsLimit * columnsMobile;
  const useMobileScroll = isCarousel || isFractionalMobile || mobileOverflow;
  const desktopOverflow = !isCarousel && !isFractionalDesktop && rowsLimit > 0 && items.length > rowsLimit * columns;
  const useDesktopScroll = isCarousel || isFractionalDesktop || desktopOverflow;
  const mobilePeek = Math.max(1.5, Math.min(8.5, Number(s.mobilePeek) || 4.5));
  const overflowPeek = Math.min(8.5, columnsMobile + 0.5);
  const effectiveMobilePeek = isCarousel
    ? mobilePeek
    : isFractionalMobile
      ? columnsMobileRaw
      : mobileOverflow
        ? overflowPeek
        : mobilePeek;
  const desktopPeek = isFractionalDesktop ? columnsRaw : Math.min(8.5, columns + 0.5);
  const uid = useMemo(() => "cc-" + Math.random().toString(36).slice(2, 8), []);

  const headerTitle = lang === "ar" ? (s.headerTitleAr || s.headerTitle) : (s.headerTitle || s.headerTitleAr);
  const headerLinkText = lang === "ar" ? (s.headerLinkTextAr || s.headerLinkText) : (s.headerLinkText || s.headerLinkTextAr);
  const headerLinkUrl = s.headerLinkUrl || "";
  const hasHeader = !!(headerTitle || headerLinkText);

  // Row caps only apply in pure-grid (non-scrolling) layouts at each breakpoint.
  const rowsCssMobile = rowsLimit > 0 && !useMobileScroll ? `\n.${uid} > *:nth-child(n+${rowsLimit * columnsMobile + 1}) { display: none; }` : "";
  const tabletColumns = _tabletCols(columnsMobile, columns);
  const rowsCssTablet = rowsLimit > 0 ? `\n@media (min-width: 768px) { .${uid} > *:nth-child(n+${rowsLimit * columnsMobile + 1}) { display: block; } .${uid} > *:nth-child(n+${rowsLimit * tabletColumns + 1}) { display: none; } }` : "";
  const rowsCssDesktop = rowsLimit > 0 && !useDesktopScroll ? `\n@media (min-width: 1024px) { .${uid} > *:nth-child(n+${rowsLimit * tabletColumns + 1}) { display: block; } .${uid} > *:nth-child(n+${rowsLimit * columns + 1}) { display: none; } }` : "";
  const rowsCssDesktopReshow = rowsLimit > 0 && useDesktopScroll ? `\n@media (min-width: 1024px) { .${uid} > *:nth-child(n+${rowsLimit * tabletColumns + 1}) { display: block; } }` : "";

  // Circles are constrained to the same content rail as every other
  // homepage block (`max-w-7xl`) so the row width matches banners,
  // product grids, and category walls above/below it. Previously the
  // section spanned the full viewport, making circles look detached
  // from the rest of the page — especially obvious on wide desktops
  // where the circle row stretched to the screen edges while the
  // products grid below was clearly narrower.
  return (
    <section className="w-full py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {hasHeader && (
          <div className="flex items-center justify-between mb-4">
            {headerTitle && <h2 className="text-lg md:text-xl font-bold" style={{ color: "var(--block-text, var(--theme-heading-color))" }}>{headerTitle}</h2>}
            {headerLinkText && headerLinkUrl ? (
              <Link href={headerLinkUrl} className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 shrink-0">
                {headerLinkText}
                <ChevronLeft className="h-4 w-4 rtl:rotate-0 ltr:rotate-180" />
              </Link>
            ) : headerLinkText ? (
              <span className="text-sm text-muted-foreground">{headerLinkText}</span>
            ) : null}
          </div>
        )}
        <style>{`
          ${useMobileScroll ? `.${uid} { scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; }` : `.${uid} { display: grid; gap: 1rem; grid-template-columns: repeat(${columnsMobile}, minmax(0, 1fr)); }`}
          /* The circle size acts as a MAXIMUM, not a hard width. In grid mode
             the cell is 1fr and at narrow tablet widths a fixed pixel size
             would overflow the cell and overlap the next column. By using
             min(Npx, 100%) the circle shrinks with the cell, and
             aspect-ratio 1/1 keeps it circular (not oval). In carousel
             mode the parent cc-snap reserves the full px width, so the
             circle still renders at full target size. */
          .${uid} .cc-circle { width: min(${circleSizeMobile}px, 100%); height: auto; aspect-ratio: 1 / 1; flex-shrink: 0; }
          ${useMobileScroll ? `.${uid} .cc-snap { flex: 0 0 calc((100% - (${effectiveMobilePeek - 0.5}) * 1rem) / ${effectiveMobilePeek}); min-width: 0; }` : `.${uid} .cc-snap { min-width: 0; }`}
          @media (min-width: 768px) { ${isCarousel ? `.${uid} .cc-snap { flex: 0 0 auto; min-width: ${circleSizeDesktop + 8}px; }` : `.${uid} { display: grid; gap: 1rem; grid-template-columns: repeat(${tabletColumns}, minmax(0, 1fr)); overflow-x: visible; scroll-snap-type: none; } .${uid} .cc-snap { flex: initial; min-width: 0; }`} .${uid} .cc-circle { width: min(${circleSizeDesktop}px, 100%); height: auto; aspect-ratio: 1 / 1; flex-shrink: 0; } }
          @media (min-width: 1024px) { ${isCarousel ? "" : useDesktopScroll ? `.${uid} { display: flex; gap: 1rem; overflow-x: auto; scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; grid-template-columns: none; } .${uid} .cc-snap { flex: 0 0 calc((100% - (${desktopPeek - 0.5}) * 1rem) / ${desktopPeek}); min-width: 0; scroll-snap-align: start; }` : `.${uid} { grid-template-columns: repeat(${columns}, minmax(0, 1fr)); }`} .${uid} .cc-circle { width: min(${circleSizeDesktop}px, 100%); height: auto; aspect-ratio: 1 / 1; flex-shrink: 0; } }${isCarousel ? "" : rowsCssMobile}${isCarousel ? "" : rowsCssTablet}${isCarousel ? "" : rowsCssDesktop}${isCarousel ? "" : rowsCssDesktopReshow}
        `}</style>
        <div
          className={(useMobileScroll || useDesktopScroll) ? `${uid} flex gap-4 overflow-x-auto pb-2 scrollbar-thin snap-x` : uid}
        >
          {items.map((it: any, idx: number) => {
            const label = lang === "ar" ? (it.titleAr || it.title) : (it.title || it.titleAr);
            const resolvedLink = it.linkType === "category" && it.categoryId
              ? `/category/${it.categoryId}`
              : it.linkType === "brand" && it.brandId
                ? `/shop?brand=${it.brandId}`
                : (it.linkUrl || "");
            const inner = (
              <div className={`flex flex-col items-center text-center gap-2 ${(useMobileScroll || useDesktopScroll) ? "snap-start cc-snap" : ""}`}>
                <div className="cc-circle rounded-full overflow-hidden border bg-muted shrink-0 mx-auto">
                  {it.imageUrl ? (
                    <img src={(resolveImageSrc(it.imageUrl, 160) as string) || (canonicalizeImageUrl(it.imageUrl) as string)} srcSet={buildAnySrcSet(it.imageUrl, [160, 320], [160, 320])} sizes="160px" alt="" width={160} height={160} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  ) : null}
                </div>
                {label && <span className="text-xs md:text-sm font-medium leading-tight line-clamp-2">{label}</span>}
              </div>
            );
            return resolvedLink ? (
              <Link key={it.id ?? idx} href={resolvedLink} className="block">{inner}</Link>
            ) : (
              <div key={it.id ?? idx}>{inner}</div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function BannerBlock({ settings }: { settings: Record<string, any> }) {
  const theme = useTheme();
  const { lang } = useI18n();
  const themeBannerRadius = (theme as any)?.bannerRadius || "0px";
  const s = settings || {};
  // Use the language-specific banner image when set; fall back to the Arabic (default) image.
  const imageUrl = (lang === "en" && s.imageUrlEn) ? s.imageUrlEn : (s.imageUrl || "");
  const linkUrl = s.linkUrl || "";
  const fullWidth = s.fullWidth === true;
  const perBlockRadius = s.imageRadius;
  const bannerRadius = perBlockRadius !== undefined && perBlockRadius !== null && perBlockRadius !== ""
    ? (typeof perBlockRadius === "number" ? `${perBlockRadius}px` : String(perBlockRadius))
    : themeBannerRadius;
  // Honor the aspectRatio admin already saves (default 16/9). Reserving the
  // box BEFORE the image loads is the single biggest win against CLS for
  // banner-heavy storefronts: previously `h-auto` meant the slot grew from
  // 0px to whatever the natural image height was once decoded, shoving
  // every section below it down the page (CLS=0.51 on prod luxurygive.com).
  const aspectRatio = Number(s.aspectRatio) > 0 ? Number(s.aspectRatio) : 16 / 9;
  const objectFit = (s.objectFit === "contain" ? "contain" : "cover") as "cover" | "contain";

  if (!imageUrl) return null;

  // When objectFit=contain we intentionally drop the fixed aspect-ratio box.
  // The merchant explicitly chose "contain" because they want the image to
  // be displayed at its natural proportions — letterboxing it inside a 16:9
  // (or other) reserved slot leaves big empty bands above/below the artwork
  // and visually inflates the section. Letting the <img> render at its
  // natural height removes those bands and tightens the spacing to just the
  // section's own py-* padding. We accept a small CLS hit here; cover mode
  // (the default) still gets the aspect-ratio reservation.
  // Width/height attrs let the browser reserve space from the aspect ratio
  // BEFORE the image bytes arrive, eliminating the 0.087 CLS spike this
  // banner caused on PageSpeed Insights (May 2026).
  const reservedW = 1600;
  const reservedH = Math.max(1, Math.round(reservedW / aspectRatio));
  const img = objectFit === "contain" ? (
    <img
      src={(resolveImageSrc(imageUrl, 1600) as string) || (canonicalizeImageUrl(imageUrl) as string)}
      srcSet={buildAnySrcSet(imageUrl, [640, 1080, 1280, 1600], [640, 1080, 1280, 1600])}
      sizes="100vw"
      alt={s.altText || s.title || s.linkText || (linkUrl ? (lang === "ar" ? `بانر — ${linkUrl}` : `Banner — ${linkUrl}`) : (lang === "ar" ? "صورة البانر" : "Banner image"))}
      width={reservedW}
      height={reservedH}
      className="block w-full h-auto"
      style={{ borderRadius: bannerRadius, objectFit: "contain" }}
      loading="eager"
      decoding="async"
      {...({ fetchPriority: "high" } as any)}
    />
  ) : (
    <div
      className="relative w-full overflow-hidden"
      style={{ aspectRatio: String(aspectRatio), borderRadius: bannerRadius }}
    >
      <img
        src={(resolveImageSrc(imageUrl, 1600) as string) || (canonicalizeImageUrl(imageUrl) as string)}
        srcSet={buildAnySrcSet(imageUrl, [640, 1080, 1280, 1600], [640, 1080, 1280, 1600])}
        sizes="100vw"
        alt={s.altText || s.title || s.linkText || (linkUrl ? (lang === "ar" ? `بانر — ${linkUrl}` : `Banner — ${linkUrl}`) : (lang === "ar" ? "صورة البانر" : "Banner image"))}
        width={reservedW}
        height={reservedH}
        className="absolute inset-0 w-full h-full block"
        style={{ objectFit }}
        loading="eager"
        decoding="async"
        {...({ fetchPriority: "high" } as any)}
      />
    </div>
  );

  const content = linkUrl ? (
    <Link href={linkUrl} className="block w-full overflow-hidden" style={{ borderRadius: bannerRadius }}>
      {img}
    </Link>
  ) : img;

  return (
    <section className="w-full py-2">
      {fullWidth ? content : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {content}
        </div>
      )}
    </section>
  );
}

function AudienceTabsBlock({ settings, lang, active, onChange, themePrimary }: { settings: Record<string, any>; lang: string; active: string; onChange: (id: string) => void; themePrimary?: string }) {
  const s = settings || {};
  const tabs: any[] = Array.isArray(s.tabs) ? s.tabs : [];
  if (tabs.length === 0) return null;

  const ICON_MAP: Record<string, any> = {
    user: User, gift: Gift, heart: Heart, tag: Tag, star: Star, "shopping-bag": ShoppingBag,
  };
  const style = s.style || "segmented";
  const mobileStyle = s.mobileStyle || "same";
  const activeColor = s.activeColor || themePrimary || "#dc2626";
  const textColor = s.textColor || "#1f2937";
  const trackColor = s.trackColor || "#f3f1ec";
  const fontSize = Number(s.fontSize ?? 15);
  const fontWeight = String(s.fontWeight || "600");
  const stickyOffset = Number(s.stickyOffset ?? 0);
  const isSticky = s.sticky !== false;
  const sectionBg = s.sectionBg || "";
  const isTransparent = !sectionBg || sectionBg === "transparent";

  // CLS fix (May 2026 luxurygive.com): previously this used
  // `useState(false)` then a useEffect to flip on mount, which caused
  // a guaranteed re-render after first paint. On real mobile devices
  // (incl. Lighthouse mobile profile), the first paint rendered the
  // desktop layout, then the effect flipped isMobile=true and
  // swapped to the mobile layout — pushing every subsequent section
  // down and accounting for the ~0.13 layout shift attributed to
  // <main>. Using the shared `useSyncExternalStore` subscription
  // (same one used at the top of Home) gives a correct synchronous
  // initial value and removes the mount-time shift.
  const isMobile = useSyncExternalStore(subscribeToMobile, getIsMobile, () => false);

  let outerClass: string;
  if (isSticky) {
    outerClass = isTransparent
      ? "sticky z-30 bg-white dark:bg-gray-950"
      : "sticky z-30 backdrop-blur";
  } else {
    outerClass = "relative z-10";
  }
  const outerStyle: any = {};
  if (isSticky) {
    outerStyle.top = `calc(var(--sticky-header-total-height, var(--theme-header-height, 0px)) + ${stickyOffset}px)`;
  }
  if (!isTransparent) {
    outerStyle.backgroundColor = sectionBg;
  }

  const effectiveStyle = (isMobile && mobileStyle === "full") ? "full" : style;

  const renderTab = (t: any, isActive: boolean) => {
    const Icon = ICON_MAP[t.icon] || null;
    const label = lang === "ar" ? (t.labelAr || t.label) : (t.label || t.labelAr);
    return { Icon, label, isActive };
  };

  let inner: ReactElement;
  if (effectiveStyle === "full") {
    inner = (
      <div className="w-full" role="tablist">
        <div className="flex items-stretch border-b" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
          {tabs.map((t: any, idx: number) => {
            const { Icon, label, isActive } = renderTab(t, t.id === active);
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => onChange(t.id)}
                className="flex-1 flex items-center justify-center gap-2 py-3 relative transition-colors"
                style={{
                  color: isActive ? activeColor : textColor,
                  fontSize,
                  fontWeight,
                  borderInlineEnd: idx < tabs.length - 1 ? "1px solid rgba(0,0,0,0.06)" : "none",
                }}
              >
                {Icon && <Icon className="h-4 w-4" />}
                <span>{label}</span>
                <span
                  aria-hidden="true"
                  className="absolute inset-x-0 bottom-0 h-[2px] transition-opacity"
                  style={{ backgroundColor: activeColor, opacity: isActive ? 1 : 0 }}
                />
              </button>
            );
          })}
        </div>
      </div>
    );
  } else if (effectiveStyle === "segmented") {
    inner = (
      <div className="max-w-md mx-auto px-2">
        <div
          className="flex items-center p-1 rounded-full transition-colors"
          style={{ backgroundColor: trackColor }}
          role="tablist"
        >
          {tabs.map((t: any) => {
            const { Icon, label, isActive } = renderTab(t, t.id === active);
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => onChange(t.id)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full transition-all duration-200"
                style={{
                  backgroundColor: isActive ? activeColor : "transparent",
                  color: isActive ? pickReadableTextColor(activeColor) : textColor,
                  fontSize,
                  fontWeight,
                  boxShadow: isActive ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                }}
              >
                {Icon && <Icon className="h-4 w-4" />}
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  } else if (effectiveStyle === "pills") {
    inner = (
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-around gap-2 py-2" role="tablist">
          {tabs.map((t: any) => {
            const { Icon, label, isActive } = renderTab(t, t.id === active);
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => onChange(t.id)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-full transition-colors"
                style={{
                  backgroundColor: isActive ? activeColor : "transparent",
                  color: isActive ? pickReadableTextColor(activeColor) : textColor,
                  border: `1px solid ${isActive ? activeColor : "rgba(0,0,0,0.1)"}`,
                  fontSize, fontWeight,
                }}
              >
                {Icon && <Icon className="h-4 w-4" />}
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  } else {
    inner = (
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-around border-b gap-6 py-1" role="tablist">
          {tabs.map((t: any) => {
            const { Icon, label, isActive } = renderTab(t, t.id === active);
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => onChange(t.id)}
                className="relative flex items-center gap-2 px-3 py-3 transition-colors"
                style={{
                  color: isActive ? activeColor : textColor,
                  fontSize, fontWeight,
                }}
              >
                {Icon && <Icon className="h-4 w-4" />}
                <span>{label}</span>
                {(effectiveStyle === "underline" || style === "underline") && (
                  <span
                    aria-hidden="true"
                    className="absolute left-0 right-0 -bottom-px h-0.5 rounded-t transition-opacity"
                    style={{ backgroundColor: activeColor, opacity: isActive ? 1 : 0 }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={outerClass} style={outerStyle}>
      {inner}
    </div>
  );
}

function PromoStripBlock({ settings, lang }: { settings: Record<string, any>; lang: string }) {
  const s = settings || {};
  const variants: any[] = Array.isArray(s.variants) ? s.variants : [];
  const [idx, setIdx] = useState(0);

  const rotateMs = Number(s.rotateMs ?? 4000) || 4000;
  useEffect(() => {
    if (variants.length < 2) return;
    const t = setInterval(() => setIdx(i => (i + 1) % variants.length), rotateMs);
    return () => clearInterval(t);
  }, [variants.length, rotateMs]);

  if (variants.length === 0) return null;

  const ICON_MAP: Record<string, any> = {
    truck: Truck, gift: Gift, megaphone: Megaphone, clock: Clock, bell: Bell, star: Star, refresh: RefreshCw,
  };
  const v = variants[idx % variants.length] || variants[0];
  const Icon = v.icon && v.icon !== "none" ? (ICON_MAP[v.icon] || Truck) : null;
  const text = (lang === "ar" ? v.textAr : v.text) || v.textAr || v.text || "";
  const justify = s.align === "start" ? "justify-start" : s.align === "end" ? "justify-end" : "justify-center";
  const showArrows = s.showArrows !== false && variants.length > 1;
  const prev = () => setIdx(i => (i - 1 + variants.length) % variants.length);
  const next = () => setIdx(i => (i + 1) % variants.length);
  const isRTL = lang === "ar";

  const inner = (
    <div className={`flex items-center gap-2 px-4 ${justify}`} style={{ flex: 1 }} dir={isRTL ? "rtl" : "ltr"}>
      {v.logoUrl
        ? <img src={v.logoUrl} alt="" className="h-5 w-auto object-contain shrink-0" />
        : Icon ? <Icon className="h-4 w-4 shrink-0" /> : null}
      <span key={idx} className="animate-in fade-in duration-500 truncate">{text}</span>
    </div>
  );

  return (
    <section
      className="w-full"
      style={{
        backgroundColor: s.bgColor || "#fdf6ec",
        color: s.textColor || "#1f2937",
        fontSize: `${Number(s.fontSize ?? 13) || 13}px`,
        fontWeight: (s.fontWeight || "600") as any,
      }}
    >
      <div
        className="max-w-7xl mx-auto flex items-center"
        style={{ height: `${Number(s.height ?? 40) || 40}px` }}
      >
        {showArrows && (
          <button onClick={isRTL ? next : prev} aria-label="prev" className="px-2 opacity-70 hover:opacity-100 transition-opacity">
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
        {v.linkUrl ? <Link href={v.linkUrl} className="contents">{inner}</Link> : inner}
        {showArrows && (
          <button onClick={isRTL ? prev : next} aria-label="next" className="px-2 opacity-70 hover:opacity-100 transition-opacity">
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </section>
  );
}

function NotificationBarsBlock({ settings, lang }: { settings: Record<string, any>; lang: string }) {
  const s = settings || {};
  const bars: any[] = Array.isArray(s.bars) ? s.bars : [];

  // Stable, content-based key for each bar so dismissals automatically
  // reset whenever the admin edits a bar (text/color/icon change). The
  // previous index-based key trapped admins: dismissing a bar during
  // testing kept it hidden in their browser even after they re-edited
  // its content from /admin/homepage-layout.
  const barKey = (bar: any): string => {
    const parts = [
      String(bar?.textAr ?? ""),
      String(bar?.text ?? ""),
      String(bar?.bgColor ?? ""),
      String(bar?.textColor ?? ""),
      String(bar?.icon ?? ""),
    ];
    return parts.join("|");
  };

  const [dismissed, setDismissed] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem("dismissed_section_bars_v2");
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  });

  // One-time migration: clear the old index-based dismissals so admins who
  // dismissed bars before this fix shipped see them again.
  useEffect(() => {
    try { localStorage.removeItem("dismissed_section_bars"); } catch {}
  }, []);

  const dismissBar = (key: string) => {
    const updated = new Set(dismissed);
    updated.add(key);
    setDismissed(updated);
    try { localStorage.setItem("dismissed_section_bars_v2", JSON.stringify([...updated])); } catch {}
  };

  const ICON_MAP: Record<string, any> = {
    truck: Truck, gift: Gift, megaphone: Megaphone, clock: Clock, bell: Bell, star: Star,
  };

  const visibleBars = bars.filter((b) => !dismissed.has(barKey(b)));
  if (visibleBars.length === 0) return null;

  return (
    <div className="w-full">
      {bars.map((bar, i) => {
        const key = barKey(bar);
        if (dismissed.has(key)) return null;
        const Icon = bar.icon && bar.icon !== "none" ? ICON_MAP[bar.icon] : null;
        const text = (lang === "ar" ? bar.textAr : bar.text) || bar.textAr || bar.text || "";
        return (
          <div
            key={`${i}-${key}`}
            className="relative flex items-center justify-center gap-2 px-4 text-center"
            style={{
              backgroundColor: bar.bgColor || "#000",
              color: bar.textColor || "#fff",
              height: `${Number(s.height ?? 32)}px`,
              fontSize: `${Number(s.fontSize ?? 11)}px`,
              fontWeight: (s.fontWeight || "500") as any,
            }}
          >
            {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
            <span className="tracking-wider">{text}</span>
            {bar.dismissible !== false && (
              <button
                onClick={() => dismissBar(key)}
                className="absolute end-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100 transition-opacity"
                aria-label={lang === "ar" ? "إغلاق الإشعار" : "Dismiss notification"}
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CategoryProductsBlock({ title, settings, lang }: { title: string; settings: Record<string, any>; lang: string }) {
  const theme = useTheme();
  const s = settings || {};
  const categoryId = s.categoryId;
  const limit = s.limit || 10;
  const displayStyle = s.displayStyle || "grid";
  const colsDesktop = Number(s.columnsPerRow) || 6;
  const colsMobile = Number(s.columnsPerRowMobile) || 2;
  const colsDesktopGrid = Math.max(1, Math.floor(colsDesktop));
  const colsMobileGrid = Math.max(1, Math.floor(colsMobile));
  const showViewAll = s.showViewAll !== false;
  const viewAllLink = s.viewAllLink || (categoryId ? `/shop?category=${categoryId}` : "/shop");
  const compact = s.compact !== false;
  const showArrows = s.showArrows !== false;
  const selectedProductIds: number[] = s.selectedProductIds || [];
  const imageShape: string = s.imageShape || "";
  const cardImageRatio: string | undefined =
    imageShape === "circle" || imageShape === "square" ? "1/1"
    : imageShape === "rectangle" ? "4/5"
    : undefined;
  // For square/rectangle, leave radius undefined so ProductCard falls back to
  // the global theme corner radius (Admin → Appearance → Corner Radius).
  // Only the circle option needs a hard override to force a full circle.
  const cardImageRadius: string | undefined =
    imageShape === "circle" ? "9999px"
    : undefined;

  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // When the merchant hand-picked a product list, fetch them by IDs
    // directly. This avoids the previous bug where the block silently
    // disappeared if any selected product sat beyond the first 50 of
    // its category, was reassigned to a different category, or went
    // out of stock under the category endpoint's status=active filter.
    if (selectedProductIds.length > 0) {
      const idsParam = selectedProductIds.slice(0, 24).join(",");
      fetch(`${BASE_URL}api/products/by-ids?ids=${idsParam}`)
        .then(r => r.json())
        .then(data => {
          setProducts(data.products || []);
          setLoading(false);
        })
        .catch(() => setLoading(false));
      return;
    }
    if (!categoryId) { setLoading(false); return; }
    fetch(`${BASE_URL}api/products?categoryId=${categoryId}&limit=${limit}&status=active&fields=card`)
      .then(r => r.json())
      .then(data => {
        setProducts(data.products || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [categoryId, limit, selectedProductIds.join(",")]);

  if (!categoryId && selectedProductIds.length === 0) return null;
  if (!loading && products.length === 0) return null;

  const gridClass = `cpgrid-m${colsMobileGrid}-d${colsDesktopGrid}`;
  const carClass = `cpcar-m${String(colsMobile).replace(".","_")}-d${String(colsDesktop).replace(".","_")}`;

  return (
    <section className={compact ? _spacing(theme?.homepageBlockSpacing).sectionTight : _spacing(theme?.homepageBlockSpacing).section}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg md:text-xl font-semibold" style={{ color: "var(--block-text, var(--theme-heading-color))" }}>{title}</h2>
          <div>
            {showViewAll && (
              <Link href={viewAllLink} className="text-xs text-gray-500 hover:text-black transition-colors px-2 py-2 inline-block min-h-[44px]" aria-label={lang === "ar" ? `عرض الكل: ${title}` : `View all: ${title}`}>
                {lang === "ar" ? "عرض الكل" : "View All"}
              </Link>
            )}
          </div>
        </div>
        {loading ? (
          <>
            <style>{_rSkel(`cpskel-m${colsMobileGrid}-d${colsDesktopGrid}`, colsMobileGrid, colsDesktopGrid)}</style>
            <div className={`cpskel-m${colsMobileGrid}-d${colsDesktopGrid}`}>
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="space-y-2">
                  <div className="aspect-square bg-gray-100 animate-pulse rounded" />
                  <div className="h-3 bg-gray-100 animate-pulse w-2/3" />
                  <div className="h-3 bg-gray-100 animate-pulse w-1/3" />
                </div>
              ))}
            </div>
          </>
        ) : displayStyle === "carousel" ? (
          <>
            <style>{_rCar(carClass, colsMobile, colsDesktop)}</style>
            <Carousel className="w-full" opts={{ align: "start", dragFree: true }}>
              <CarouselContent className={`-ml-3 ${carClass}`}>
                {products.map((product, idx) => (
                  <CarouselItem key={product.id} className="pl-3">
                    <SharedProductCard product={product} priority={idx < 4} imageRatio={cardImageRatio} imageRadius={cardImageRadius} />
                  </CarouselItem>
                ))}
              </CarouselContent>
              {showArrows && <CarouselPrevious className="-left-4 bg-white border shadow-md" srLabel={lang === "ar" ? `السابق: ${title}` : `Previous: ${title}`} />}
              {showArrows && <CarouselNext className="-right-4 bg-white border shadow-md" srLabel={lang === "ar" ? `التالي: ${title}` : `Next: ${title}`} />}
            </Carousel>
          </>
        ) : (
          <>
            <style>{_rGrid(gridClass, colsMobileGrid, colsDesktopGrid)}</style>
            <div className={`grid gap-3 md:gap-4 ${gridClass}`}>
              {products.map((product, idx) => (
                <SharedProductCard key={product.id} product={product} priority={idx < 4} imageRatio={cardImageRatio} imageRadius={cardImageRadius} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function TabbedCategoriesBlock({ settings, lang }: { settings: Record<string, any>; lang: string }) {
  const theme = useTheme();
  const s = settings || {};
  const tabs: { label: string; labelAr?: string; categoryId: number; selectedProductIds?: number[]; viewAllLink?: string }[] = s.tabs || [];
  const limit = s.limit || 8;
  const colsDesktop = Number(s.columnsPerRow) || 3.5;
  const colsMobile = Number(s.columnsPerRowMobile) || 2;
  const imageShape: string = s.imageShape || "";
  const cardImageRatio: string | undefined =
    imageShape === "circle" || imageShape === "square" ? "1/1"
    : imageShape === "rectangle" ? "4/5"
    : undefined;
  const cardImageRadius: string | undefined =
    imageShape === "circle" ? "9999px"
    : undefined;
  const colsDesktopGrid = Math.max(1, Math.floor(colsDesktop));
  const colsMobileGrid = Math.max(1, Math.floor(colsMobile));
  const displayStyle = s.displayStyle || "carousel";
  const showArrows = s.showArrows !== false;
  // Accept any CSS color string the merchant types (including
  // "transparent", hex, rgb()). Empty / undefined => no background
  // override (theme default shows through). The legacy "gray" preset is
  // mapped to a neutral light gray so old saved blocks keep working.
  const rawBg = (s.bgColor ?? "").toString().trim();
  const bgClass = rawBg === "gray" ? "bg-gray-50" : "";
  const bgInlineStyle: React.CSSProperties = rawBg && rawBg !== "gray"
    ? { backgroundColor: rawBg }
    : {};

  const [activeTab, setActiveTab] = useState(0);
  const [tabProducts, setTabProducts] = useState<Record<number, any[]>>({});
  const [loadingTab, setLoadingTab] = useState<number | null>(null);

  const fetchTabProducts = useCallback((tabIndex: number) => {
    const tab = tabs[tabIndex];
    if (!tab || tabProducts[tabIndex]) return;
    setLoadingTab(tabIndex);
    const fetchLimit = (tab.selectedProductIds?.length || 0) > 0 ? 50 : limit;
    fetch(`${BASE_URL}api/products?categoryId=${tab.categoryId}&limit=${fetchLimit}&status=active&fields=card`)
      .then(r => r.json())
      .then(data => {
        let items = data.products || [];
        if (tab.selectedProductIds && tab.selectedProductIds.length > 0) {
          const idSet = new Set(tab.selectedProductIds);
          const filtered = items.filter((p: any) => idSet.has(p.id));
          items = tab.selectedProductIds.map(id => filtered.find((p: any) => p.id === id)).filter(Boolean);
        }
        setTabProducts(prev => ({ ...prev, [tabIndex]: items }));
        setLoadingTab(null);
      })
      .catch(() => setLoadingTab(null));
  }, [tabs, limit, tabProducts]);

  useEffect(() => {
    if (tabs.length > 0) fetchTabProducts(0);
  }, [tabs.length]);

  useEffect(() => {
    fetchTabProducts(activeTab);
  }, [activeTab]);

  if (tabs.length === 0) return null;

  const products = tabProducts[activeTab] || [];
  const isLoading = loadingTab === activeTab;

  return (
    <section className={`${_spacing(theme?.homepageBlockSpacing).sectionTight} ${bgClass}`} style={bgInlineStyle}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Tab strip + view-all link */}
        <div className="flex items-center justify-between mb-4 border-b border-gray-200">
          <div className="flex items-center gap-4 md:gap-6 overflow-x-auto pb-0 flex-1 min-w-0">
            {tabs.map((tab, i) => {
              const label = lang === "ar" ? (tab.labelAr || tab.label) : tab.label;
              return (
                <button
                  key={i}
                  onClick={() => setActiveTab(i)}
                  className={`whitespace-nowrap pb-3 px-1 text-sm md:text-base font-medium border-b-2 transition-colors flex-shrink-0 ${
                    activeTab === i
                      ? "border-black text-black"
                      : "border-transparent text-gray-400 hover:text-gray-600"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          {tabs[activeTab]?.viewAllLink && (
            <Link
              href={tabs[activeTab].viewAllLink!}
              className="flex-shrink-0 text-xs text-gray-500 hover:text-black transition-colors pb-3 px-2 min-h-[44px] flex items-center"
              aria-label={lang === "ar" ? "عرض الكل" : "View All"}
            >
              {lang === "ar" ? "عرض الكل" : "View All"}
            </Link>
          )}
        </div>
        {isLoading ? (
          <>
            <style>{_rSkel(`tcskel-m${colsMobileGrid}-d${colsDesktopGrid}`, colsMobileGrid, colsDesktopGrid)}</style>
            <div className={`tcskel-m${colsMobileGrid}-d${colsDesktopGrid}`}>
              {Array.from({ length: colsDesktopGrid }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <div className="aspect-square bg-gray-100 animate-pulse rounded" />
                  <div className="h-3 bg-gray-100 animate-pulse w-2/3" />
                  <div className="h-3 bg-gray-100 animate-pulse w-1/3" />
                </div>
              ))}
            </div>
          </>
        ) : displayStyle === "carousel" ? (
          <>
            <style>{_rCar(`tccar-m${String(colsMobile).replace(".","_")}-d${String(colsDesktop).replace(".","_")}`, colsMobile, colsDesktop)}</style>
            <Carousel className="w-full" opts={{ align: "start", dragFree: true }}>
              <CarouselContent className={`-ml-3 tccar-m${String(colsMobile).replace(".","_")}-d${String(colsDesktop).replace(".","_")}`}>
                {products.map((product: any, idx: number) => (
                  <CarouselItem key={product.id} className="pl-3">
                    <SharedProductCard product={product} priority={idx < 4} imageRatio={cardImageRatio} imageRadius={cardImageRadius} />
                  </CarouselItem>
                ))}
              </CarouselContent>
              {showArrows && <CarouselPrevious className="-left-4 bg-white border shadow-md" srLabel={lang === "ar" ? "تبويبات الفئات: السابق" : "Category tabs: Previous"} />}
              {showArrows && <CarouselNext className="-right-4 bg-white border shadow-md" srLabel={lang === "ar" ? "تبويبات الفئات: التالي" : "Category tabs: Next"} />}
            </Carousel>
          </>
        ) : (
          <>
            <style>{_rGrid(`tcgrid-m${colsMobileGrid}-d${colsDesktopGrid}`, colsMobileGrid, colsDesktopGrid)}</style>
            <div className={`grid gap-3 md:gap-4 tcgrid-m${colsMobileGrid}-d${colsDesktopGrid}`}>
              {products.slice(0, limit).map((product: any, idx: number) => (
                <SharedProductCard key={product.id} product={product} priority={idx < 4} imageRatio={cardImageRatio} imageRadius={cardImageRadius} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function BannerProductsBlock({ title, settings, lang, isFirst = false }: { title: string; settings: Record<string, any>; lang: string; isFirst?: boolean }) {
  const theme = useTheme();
  const s = settings || {};
  const colsDesktop = Number(s.columnsPerRow) || 2;
  const colsMobile = Number(s.columnsPerRowMobile) || 1.5;
  // "start" = beginning of reading direction (RIGHT in RTL/Arabic, LEFT in LTR/English)
  // Auto-flip: in RTL, "start" maps to first DOM child which flexbox places on the right.
  // If bannerPosition is "end", keep it as second child (LEFT in RTL, RIGHT in LTR).
  const bannerPos = s.bannerPosition || "start";
  const categoryId = s.categoryId;
  const limit = s.limit || 8;
  const showViewAll = s.showViewAll !== false;
  const viewAllLink = s.viewAllLink || (categoryId ? `/shop?category=${categoryId}` : "/shop");
  const selectedProductIds: number[] = s.selectedProductIds || [];
  const compact = s.compact !== false;

  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedProductIds.length > 0) {
      const idsParam = selectedProductIds.slice(0, 24).join(",");
      fetch(`${BASE_URL}api/products/by-ids?ids=${idsParam}`)
        .then(r => r.json())
        .then(data => { setProducts(data.products || []); setLoading(false); })
        .catch(() => setLoading(false));
      return;
    }
    if (!categoryId) { setLoading(false); return; }
    fetch(`${BASE_URL}api/products?categoryId=${categoryId}&limit=${limit}&status=active&fields=card`)
      .then(r => r.json())
      .then(data => { setProducts(data.products || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [categoryId, limit, selectedProductIds.join(",")]);

  const themeBannerRadius = (theme as any)?.bannerRadius || "0px";
  const perBlockRadius = s.imageRadius;
  const bannerRadius = perBlockRadius !== undefined && perBlockRadius !== null && perBlockRadius !== ""
    ? (typeof perBlockRadius === "number" ? `${perBlockRadius}px` : String(perBlockRadius))
    : themeBannerRadius;

  const imageUrl = (lang === "en" && s.imageUrlEn) ? s.imageUrlEn : (s.imageUrl || "");
  const linkUrl = s.linkUrl || "";
  const boldText = lang === "ar" ? (s.titleBoldAr || s.titleBold || "") : (s.titleBold || s.titleBoldAr || "");
  const normalText = lang === "ar" ? (s.titleNormalAr || s.titleNormal || "") : (s.titleNormal || s.titleNormalAr || "");
  const textColor = s.textColor || "#ffffff";
  const textPos = s.textPosition || "top-start";

  // Default to carousel mode (banner scrolls with products).
  // Only fall back to the fixed side-banner layout when explicitly disabled.
  const bannerInCarousel = s.bannerInCarousel !== false;

  const bpCls = `bpbl-d${String(colsDesktop).replace(".", "_")}-m${String(colsMobile).replace(".", "_")}`;
  const carCls = `bpcar-d${String(colsDesktop).replace(".", "_")}-m${String(colsMobile).replace(".", "_")}`;

  const hasProductSource = categoryId || selectedProductIds.length > 0;
  if (!imageUrl && !hasProductSource) return null;
  const showProducts = hasProductSource && (loading || products.length > 0);

  const posMap: Record<string, string> = {
    "top-start": "top-0 start-0",
    "top-end": "top-0 end-0",
    "center": "inset-0 flex items-center justify-center",
    "bottom-start": "bottom-0 start-0",
    "bottom-end": "bottom-0 end-0",
  };

  // Shared banner overlay content
  const bannerOverlay = (boldText || normalText) ? (
    <div
      className={`absolute inset-x-0 p-3 flex flex-col gap-1 ${
        textPos === "center"
          ? "inset-y-0 items-center justify-center"
          : textPos.startsWith("top")
          ? `top-0 ${textPos.endsWith("end") ? "items-end" : "items-start"}`
          : `bottom-0 ${textPos.endsWith("end") ? "items-end" : "items-start"}`
      }`}
      style={{
        background: textPos === "center"
          ? "rgba(0,0,0,0.35)"
          : textPos.startsWith("top")
          ? "linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, transparent 100%)"
          : "linear-gradient(to top, rgba(0,0,0,0.65) 0%, transparent 100%)"
      }}
    >
      {boldText && <p className="font-bold leading-snug" style={{ color: textColor, fontSize: s.boldFontSize ? `${s.boldFontSize}px` : "14px" }}>{boldText}</p>}
      {normalText && <p className="leading-snug opacity-90" style={{ color: textColor, fontSize: s.normalFontSize ? `${s.normalFontSize}px` : "12px" }}>{normalText}</p>}
    </div>
  ) : null;

  // ── Mode: banner as first carousel item (scrollable) ──────────────────────
  if (bannerInCarousel) {
    return (
      <section className={compact ? _spacing(theme?.homepageBlockSpacing).sectionTight : _spacing(theme?.homepageBlockSpacing).section}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {title && (
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg md:text-xl font-semibold" style={{ color: "var(--block-text, var(--theme-heading-color))" }}>{title}</h2>
              {showViewAll && (
                <Link href={viewAllLink} className="text-xs text-gray-500 hover:text-black transition-colors px-2 py-2 inline-block min-h-[44px]" aria-label={lang === "ar" ? `عرض الكل: ${title}` : `View all: ${title}`}>
                  {lang === "ar" ? "عرض الكل" : "View All"}
                </Link>
              )}
            </div>
          )}
          <style>{_rCar(carCls, colsMobile, colsDesktop)}</style>
          {loading ? (
            <div className={`flex gap-3`}>
              {[1, 2, 3].map(i => <div key={i} className="flex-1 aspect-[3/4] bg-gray-100 animate-pulse rounded" style={{ flex: `0 0 ${100 / colsMobile}%`, maxWidth: `${100 / colsMobile}%` }} />)}
            </div>
          ) : (
            <Carousel opts={{ align: "start", dragFree: true }}>
              <CarouselContent className={`-ml-3 ${carCls}`}>
                {imageUrl && (
                  // flex flex-col so that flex-1 on the inner div works —
                  // the CarouselItem stretches to the row height (tallest sibling
                  // = product card incl. text + button), flex-col propagates
                  // that height down so the image fills the entire card area.
                  <CarouselItem className="pl-3 flex flex-col">
                    <div className="relative overflow-hidden w-full flex-1" style={{ borderRadius: bannerRadius, minHeight: "200px" }}>
                      <img
                        src={imageUrl}
                        alt={boldText || (lang === "ar" ? "بنر" : "Banner")}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading={isFirst ? "eager" : "lazy"}
                        decoding="async"
                        {...(isFirst ? { fetchPriority: "high" } as any : {})}
                      />
                      {bannerOverlay}
                      {linkUrl && <Link href={linkUrl} className="absolute inset-0 z-10" aria-label={boldText || (lang === "ar" ? "تصفح البنر" : "View banner")} />}
                    </div>
                  </CarouselItem>
                )}
                {products.map((product, idx) => (
                  <CarouselItem key={product.id} className="pl-3 h-full">
                    <SharedProductCard product={product} priority={idx < 4} />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          )}
        </div>
      </section>
    );
  }

  // ── Mode: fixed side banner + product carousel (original) ─────────────────
  const bannerCard = (
    <div className="bp-bnr relative overflow-hidden flex-shrink-0" style={{ borderRadius: bannerRadius, minHeight: "200px" }}>
      {imageUrl && (
        <img
          src={imageUrl}
          alt={boldText || (lang === "ar" ? "بنر" : "Banner")}
          className="absolute inset-0 w-full h-full object-cover"
          loading={isFirst ? "eager" : "lazy"}
          decoding="async"
          {...(isFirst ? { fetchPriority: "high" } as any : {})}
        />
      )}
      {bannerOverlay}
      {linkUrl && <Link href={linkUrl} className="absolute inset-0 z-10" aria-label={boldText || (lang === "ar" ? "تصفح البنر" : "View banner")} />}
    </div>
  );

  return (
    <section className={compact ? _spacing(theme?.homepageBlockSpacing).sectionTight : _spacing(theme?.homepageBlockSpacing).section}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {title && (
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg md:text-xl font-semibold" style={{ color: "var(--block-text, var(--theme-heading-color))" }}>{title}</h2>
            {showViewAll && (
              <Link href={viewAllLink} className="text-xs text-gray-500 hover:text-black transition-colors px-2 py-2 inline-block min-h-[44px]" aria-label={lang === "ar" ? `عرض الكل: ${title}` : `View all: ${title}`}>
                {lang === "ar" ? "عرض الكل" : "View All"}
              </Link>
            )}
          </div>
        )}
        {showProducts && <style>{_rBP(bpCls, colsMobile, colsDesktop)}</style>}
        {showProducts && <style>{_rCar(carCls, colsMobile, colsDesktop)}</style>}
        <div className={showProducts ? bpCls : ""}>
          {bannerPos === "start" && bannerCard}
          {showProducts && (
            <div className="bp-prods">
              {loading ? (
                <div className="flex gap-3 h-full">
                  {[1, 2].map(i => <div key={i} className="flex-1 aspect-[3/4] bg-gray-100 animate-pulse rounded" />)}
                </div>
              ) : (
                <Carousel className="w-full h-full" opts={{ align: "start", dragFree: true }}>
                  <CarouselContent className={`-ml-3 ${carCls}`}>
                    {products.map((product, idx) => (
                      <CarouselItem key={product.id} className="pl-3 h-full">
                        <SharedProductCard product={product} priority={idx < 4} />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>
              )}
            </div>
          )}
          {bannerPos !== "start" && bannerCard}
        </div>
      </div>
    </section>
  );
}

function MultiBannersBlock({ settings, lang }: { settings: Record<string, any>; lang: string }) {
  const theme = useTheme();
  const s = settings || {};
  const banners: any[] = Array.isArray(s.banners) ? s.banners : [];
  const colsDesktop = Number(s.columnsPerRow) || 2;
  const colsMobile = Number(s.columnsPerRowMobile) || 1.5;
  const displayStyle = s.displayStyle || "carousel";
  const compact = s.compact !== false;
  const showArrows = s.showArrows === true;
  const colsDesktopGrid = Math.max(1, Math.floor(colsDesktop));
  const colsMobileGrid = Math.max(1, Math.floor(colsMobile));
  const aspectRatio = s.aspectRatio === "auto" ? null : (Number(s.aspectRatio) > 0 ? Number(s.aspectRatio) : 16 / 9);

  const themeBannerRadius = (theme as any)?.bannerRadius || "0px";
  const perBlockRadius = s.imageRadius;
  const bannerRadius = perBlockRadius !== undefined && perBlockRadius !== null && perBlockRadius !== ""
    ? (typeof perBlockRadius === "number" ? `${perBlockRadius}px` : String(perBlockRadius))
    : themeBannerRadius;

  if (banners.length === 0) return null;

  const carCls = `mbcar-d${String(colsDesktop).replace(".", "_")}-m${String(colsMobile).replace(".", "_")}`;
  const gridCls = `mbgrid-d${colsDesktopGrid}-m${colsMobileGrid}`;

  const renderBannerCard = (b: any, idx: number) => {
    const url = (lang === "en" && b.imageUrlEn) ? b.imageUrlEn : (b.imageUrl || "");
    const alt = (lang === "ar" ? (b.altTextAr || b.altText || "") : (b.altText || b.altTextAr || "")) || (lang === "ar" ? "بنر" : "Banner");
    const inner = aspectRatio === null ? (
      <div style={{ borderRadius: bannerRadius, overflow: "hidden" }}>
        {url ? (
          <img
            src={url}
            alt={alt}
            className="w-full h-auto block"
            loading={idx < 4 ? "eager" : "lazy"}
            decoding="async"
            style={{ borderRadius: bannerRadius }}
          />
        ) : (
          <div className="w-full aspect-video bg-gray-100" style={{ borderRadius: bannerRadius }} />
        )}
      </div>
    ) : (
      <div className="relative overflow-hidden w-full" style={{ aspectRatio: String(aspectRatio), borderRadius: bannerRadius }}>
        {url ? (
          <img
            src={url}
            alt={alt}
            className="absolute inset-0 w-full h-full object-cover"
            loading={idx < 4 ? "eager" : "lazy"}
            decoding={idx === 0 ? "sync" : "async"}
            {...(idx === 0 ? { fetchPriority: "high" } as any : {})}
            style={{ borderRadius: bannerRadius }}
          />
        ) : (
          <div className="absolute inset-0 bg-gray-100" style={{ borderRadius: bannerRadius }} />
        )}
      </div>
    );
    return b.linkUrl ? (
      <Link key={idx} href={b.linkUrl} className="block" style={{ borderRadius: bannerRadius }}>{inner}</Link>
    ) : <div key={idx}>{inner}</div>;
  };

  return (
    <section className={compact ? _spacing(theme?.homepageBlockSpacing).sectionTight : _spacing(theme?.homepageBlockSpacing).section}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {displayStyle === "carousel" ? (
          <>
            <style>{_rCar(carCls, colsMobile, colsDesktop)}</style>
            <Carousel className="w-full" opts={{ align: "start", dragFree: true }}>
              <CarouselContent className={`-ml-3 ${carCls}`}>
                {banners.map((b, idx) => (
                  <CarouselItem key={idx} className="pl-3">
                    {renderBannerCard(b, idx)}
                  </CarouselItem>
                ))}
              </CarouselContent>
              {showArrows && <CarouselPrevious className="-left-4 bg-white border shadow-md" srLabel={lang === "ar" ? "السابق" : "Previous"} />}
              {showArrows && <CarouselNext className="-right-4 bg-white border shadow-md" srLabel={lang === "ar" ? "التالي" : "Next"} />}
            </Carousel>
          </>
        ) : (
          <>
            <style>{_rGrid(gridCls, colsMobileGrid, colsDesktopGrid)}</style>
            <div className={`grid gap-3 md:gap-4 ${gridCls}`}>
              {banners.map((b, idx) => renderBannerCard(b, idx))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function AppDownloadBlock({ settings, lang }: { settings: Record<string, any>; lang: string }) {
  const s = settings || {};
  const bannerUrl = s.bannerImageUrl || "";
  const appleUrl = s.appleStoreUrl || "#";
  const googleUrl = s.googlePlayUrl || "#";
  const appleImg = s.appleImageUrl || "";
  const googleImg = s.googleImageUrl || "";
  const appImg = s.appPreviewImageUrl || "";

  return (
    <section className="w-full py-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {bannerUrl && <img src={bannerUrl} alt={s.bannerAlt || "Mobile App"} className="w-full block mb-4" loading="lazy" decoding="async" />}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 items-center">
          {appleImg && (
            <a href={appleUrl} target="_blank" rel="noopener noreferrer" className="block">
              <img src={appleImg} alt="App Store" className="w-full rounded-lg" loading="lazy" decoding="async" />
            </a>
          )}
          {googleImg && (
            <a href={googleUrl} target="_blank" rel="noopener noreferrer" className="block">
              <img src={googleImg} alt="Google Play" className="w-full rounded-lg" loading="lazy" decoding="async" />
            </a>
          )}
          {appImg && (
            <div className="hidden md:block">
              <img src={appImg} alt={s.appPreviewAlt || "App preview"} className="w-full rounded-lg" loading="lazy" decoding="async" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function WhyUs({ lang, settings, title }: { lang: string; settings?: Record<string, any>; title?: string }) {
  const theme = useTheme();
  const s = settings || {};
  const ICON_MAP: Record<string, any> = { shield: ShieldCheck, truck: Truck, credit: CreditCard, star: ShieldCheck, check: ShieldCheck };
  const cols = s.columns || 3;
  const bgColor = s.bgColor || "white";
  const bgClass = bgColor === "dark" ? "bg-black text-white" : bgColor === "gray" ? "bg-gray-100" : "";
  const defaultItems = [
    { iconType: "shield", titleEn: "3 Year Warranty", titleAr: "ضمان 3 سنوات", descEn: "Three-year exchange warranty on all our products", descAr: "ضمان استبدال لمدة ثلاث سنوات على جميع منتجاتنا" },
    { iconType: "truck", titleEn: "Free Shipping", titleAr: "شحن مجاني", descEn: "Free shipping on all orders within Saudi Arabia", descAr: "شحن مجاني لجميع الطلبات داخل المملكة العربية السعودية" },
    { iconType: "credit", titleEn: "Interest-Free Installments", titleAr: "تقسيط بدون فوائد", descEn: "Split your purchase interest-free via Tabby & Tamara", descAr: "قسّط مشترياتك بدون فوائد عبر تابي وتمارا" },
  ];
  const items = s.items && s.items.length > 0 ? s.items : defaultItems;
  const sectionTitle = title || (lang === "ar" ? "لماذا نحن؟" : "Why Us?");

  return (
    <section className={`${_spacing(theme?.homepageBlockSpacing).section} border-b border-gray-100 ${bgClass}`}>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="text-center mb-6 md:mb-12">
          <h2 className="text-2xl md:text-3xl font-light tracking-[0.2em] uppercase mb-3" style={{ color: "var(--block-text, var(--theme-heading-color))" }}>{sectionTitle}</h2>
          <div className="w-16 h-[1px] bg-black mx-auto" />
        </div>
        <style>{_rGrid("whyus-grid", 1, cols)}</style>
        <div className="whyus-grid grid gap-10 max-w-4xl mx-auto">
          {items.map((item: any, idx: number) => {
            const IconComp = ICON_MAP[item.iconType] || ShieldCheck;
            return (
              <div key={idx} className="text-center">
                <div className={`w-16 h-16 mx-auto mb-4 border rounded-full flex items-center justify-center ${bgColor === "dark" ? "border-gray-600" : "border-gray-200"}`}>
                  <IconComp className={`h-7 w-7 ${bgColor === "dark" ? "text-gray-300" : "text-gray-700"}`} />
                </div>
                <h3 className="text-sm tracking-[0.15em] uppercase font-medium mb-2">
                  {lang === "ar" ? (item.titleAr || item.titleEn) : item.titleEn}
                </h3>
                <p className={`text-xs leading-relaxed ${bgColor === "dark" ? "text-gray-400" : "text-gray-500"}`}>
                  {lang === "ar" ? (item.descAr || item.descEn) : item.descEn}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

